// Mock data driven by El-API domain (Upload, Session, BodyMeasures, Sport)
// Sport ids mirror ActivityId enum in NetCoreInterfaces (subset).
// Movement primitives + use-case framing inspired by AIKYNETIX brief
// (run / lift / jump / mobility, multi-athlete dashboards, API integration).

import {
  FOOTBALL_GROUP, FOOTBALL_EMOJI, FOOTBALL_ACTIVITY_META, FOOTBALL_ACTIVITIES, FOOTBALL_PHASES,
  type FootballActivityId,
} from "./constants/football";

export type ActivityId =
  | "golfSwingFaceOn"
  | "golfSwingDTL"
  | "golfPuttingFaceOn"
  | "golfPuttingDTL"
  | "tennisServeDTL"
  | "tennisServeFaceOn"
  | "tennisForehand"
  | "tennisBackhand"
  | "cricketBatting"
  | "cricketBowlingDtl"
  | "cricketBowlingFront"
  | "bowling"
  | "runGait"
  | "verticalJump"
  | "verticalJumpFront"
  | "squatLift"
  | "squatFront"
  | "squatBehind"
  | "burpeeSide"
  | "pullUpFront"
  | "pullUpSide"
  | "lungeSide"
  | "lungeFront"
  | "pushUpSide"
  | "pushUpFront"
  // Workplace ergonomic task templates (NIOSH-Lifting, REBA, RULA)
  | "wpLift"
  | "wpPushPull"
  | "wpOverhead"
  | "wpRepetitive"
  | "wpLadder"
  // Dance — TikTok-trend choreographies, leaderboard-driven inside Explore
  | "danceRenegade"
  | "danceSavage"
  | "danceLevitating"
  | "danceToosie"
  | "danceOutWest"
  | "danceWap"
  | "danceIconicByMistake"
  // Skateboarding (side view)
  | "skateOllie"
  | "skateKickflip"
  | "skateDropIn"
  // Football set-pieces (struck dead ball) — see constants/football.ts
  | FootballActivityId
  // Yoga static holds (front and side views)
  | "yogaWarrior2Front"
  | "yogaWarrior2Side"
  // Yoga static holds (side and front views)
  | "yogaDownwardDogSide"
  | "yogaDownwardDogFront"
  // Yoga static hold on the mat (side view only)
  | "yogaCobraSide"
  // Boxing combinations (side and front views)
  | "boxingJabCrossSide"
  | "boxingJabCrossFront"
  // Yoga, Plank (side view only — every cue of a plank is sagittal)
  | "yogaPlankSide"
  // Yoga Chair pose (side and front views)
  | "yogaChairSide"
  | "yogaChairFront"
  | "basketballFreeThrowSide"
  | "basketballFreeThrowFront"
  | "yogaTriangleFront"
  | "yogaTriangleSide"
  | "yogaBridgeSide"
  | "yogaTreeFront"
  | "yogaTreeSide"
  // Volleyball, baseball and the deadlift — a technique per camera
  | "volleyballServeSide"
  | "volleyballServeFront"
  | "baseballPitchSide"
  | "baseballPitchFront"
  | "deadliftSide"
  | "deadliftFront"
  // Fallback when a sport can't be resolved. It must NOT masquerade as a real sport — previously
  // unresolved uploads were silently shown as "golfSwingFaceOn".
  | "unknown";

export const ACTIVITIES: { id: ActivityId; label: string; group: string; positions: number; emoji: string }[] = [
  { id: "golfSwingFaceOn",   label: "Golf — Swing (Face-On)",   group: "Golf",    positions: 10, emoji: "🏌️" },
  { id: "golfSwingDTL",      label: "Golf — Swing (DTL)",        group: "Golf",    positions: 10, emoji: "🏌️" },
  { id: "golfPuttingFaceOn", label: "Golf — Putting (Face-On)",  group: "Golf",    positions: 6,  emoji: "⛳" },
  { id: "golfPuttingDTL",    label: "Golf — Putting (DTL)",      group: "Golf",    positions: 6,  emoji: "⛳" },
  { id: "tennisServeFaceOn", label: "Tennis — Serve (Face-On)",  group: "Tennis",  positions: 8,  emoji: "🎾" },
  { id: "tennisServeDTL",    label: "Tennis — Serve (DTL)",      group: "Tennis",  positions: 8,  emoji: "🎾" },
  { id: "tennisForehand",    label: "Tennis — Forehand",         group: "Tennis",  positions: 6,  emoji: "🎾" },
  { id: "tennisBackhand",    label: "Tennis — Backhand",         group: "Tennis",  positions: 6,  emoji: "🎾" },
  // Cricket bowling, one entry per ANGLE: two angles of one action are two techniques (their own
  // SetFinder each), and five phases, which is what SetFinder_Cricket_Bowling actually finds.
  { id: "cricketBowlingDtl",   label: "Cricket — Bowling (DTL)",   group: "Cricket", positions: 5,  emoji: "🏏" },
  { id: "cricketBowlingFront", label: "Cricket — Bowling (Front)", group: "Cricket", positions: 5,  emoji: "🏏" },
  { id: "cricketBatting",    label: "Cricket — Batting",         group: "Cricket", positions: 5,  emoji: "🏏" },
  { id: "bowling",           label: "Bowling — Delivery",        group: "Bowling", positions: 6,  emoji: "🎳" },
  { id: "runGait",           label: "Run — Gait Cycle",          group: "Movement", positions: 4, emoji: "🏃" },
  { id: "volleyballServeSide",  label: "Volleyball — Serve (Side)",  group: "Volleyball", positions: 4, emoji: "🏐" },
  { id: "volleyballServeFront", label: "Volleyball — Serve (Front)", group: "Volleyball", positions: 4, emoji: "🏐" },
  { id: "baseballPitchSide",    label: "Baseball — Pitch (Side)",    group: "Baseball", positions: 4, emoji: "⚾" },
  { id: "baseballPitchFront",   label: "Baseball — Pitch (Front)",   group: "Baseball", positions: 4, emoji: "⚾" },
  { id: "deadliftSide",         label: "Deadlift — Side View",       group: "Movement", positions: 3, emoji: "🏋️" },
  { id: "deadliftFront",        label: "Deadlift — Front View",      group: "Movement", positions: 3, emoji: "🏋️" },
  { id: "squatFront",        label: "Squat — Front View",        group: "Movement", positions: 3, emoji: "🏋️" },
  { id: "squatBehind",       label: "Squat — Behind View",       group: "Movement", positions: 3, emoji: "🏋️" },
  { id: "burpeeSide",        label: "Burpee — Side View",        group: "Movement", positions: 3, emoji: "🔥" },
  { id: "pullUpFront",       label: "Pull-up — Front View",      group: "Movement", positions: 3, emoji: "🧗" },
  { id: "pullUpSide",        label: "Pull-up — Side View",       group: "Movement", positions: 3, emoji: "🧗" },
  { id: "lungeSide",         label: "Lunge — Side View",         group: "Movement", positions: 3, emoji: "🏋️" },
  { id: "lungeFront",        label: "Lunge — Front View",        group: "Movement", positions: 3, emoji: "🏋️" },
  { id: "pushUpSide",        label: "Push-Up — Side View",       group: "Movement", positions: 3, emoji: "💪" },
  { id: "pushUpFront",       label: "Push-Up — Front View",      group: "Movement", positions: 3, emoji: "💪" },
  { id: "verticalJump",      label: "Vertical Jump — Side View",  group: "Movement", positions: 5, emoji: "🤾" },
  { id: "verticalJumpFront", label: "Vertical Jump — Front View", group: "Movement", positions: 5, emoji: "🤾" },
  { id: "squatLift",         label: "Squat — Side View",         group: "Movement", positions: 3, emoji: "🏋️" },
  // Workplace task templates
  { id: "wpLift",       label: "Manual lift / lower",     group: "Workplace", positions: 6, emoji: "📦" },
  { id: "wpPushPull",   label: "Push / pull cart",        group: "Workplace", positions: 4, emoji: "🛒" },
  { id: "wpOverhead",   label: "Overhead reach",          group: "Workplace", positions: 4, emoji: "🙆" },
  { id: "wpRepetitive", label: "Repetitive assembly",     group: "Workplace", positions: 4, emoji: "🔧" },
  { id: "wpLadder",     label: "Ladder climb",            group: "Workplace", positions: 4, emoji: "🪜" },
  // Dance templates — each maps to a TikTok-trend choreography
  { id: "danceRenegade",   label: "Dance — Renegade",     group: "Dance", positions: 5, emoji: "💃" },
  { id: "danceSavage",     label: "Dance — Savage",       group: "Dance", positions: 5, emoji: "🕺" },
  { id: "danceLevitating", label: "Dance — Levitating",   group: "Dance", positions: 5, emoji: "💃" },
  { id: "danceToosie",     label: "Dance — Toosie Slide", group: "Dance", positions: 4, emoji: "🕺" },
  { id: "danceOutWest",    label: "Dance — Out West",     group: "Dance", positions: 6, emoji: "💃" },
  { id: "danceWap",        label: "Dance — WAP",          group: "Dance", positions: 4, emoji: "🕺" },
  { id: "danceIconicByMistake", label: "Dance — Iconic by Mistake", group: "Dance", positions: 9, emoji: "💃" },
  // Skateboarding templates (side view)
  { id: "skateOllie",     label: "Skate — Ollie",        group: "Skateboarding", positions: 5, emoji: "🛹" },
  { id: "skateKickflip",  label: "Skate — Kickflip",     group: "Skateboarding", positions: 5, emoji: "🛹" },
  { id: "skateDropIn",    label: "Skate — Drop-in",      group: "Skateboarding", positions: 4, emoji: "🛹" },
  // Football set-pieces — see constants/football.ts
  ...FOOTBALL_ACTIVITY_META.map((a) => ({ id: a.id, label: a.label, group: FOOTBALL_GROUP, positions: a.positions, emoji: FOOTBALL_EMOJI })),
  // Yoga static holds — entry, hold, release (mirrors SetFinder_Yoga)
  { id: "yogaWarrior2Front", label: "Yoga — Warrior II (Front)", group: "Yoga", positions: 3, emoji: "🧘" },
  { id: "yogaWarrior2Side",  label: "Yoga — Warrior II (Side)",  group: "Yoga", positions: 3, emoji: "🧘" },
  // Yoga static holds — entry, hold, release (mirrors SetFinder_YogaDownwardDog)
  { id: "yogaDownwardDogSide",  label: "Yoga — Downward Dog (Side)",  group: "Yoga", positions: 3, emoji: "🧘" },
  { id: "yogaDownwardDogFront", label: "Yoga — Downward Dog (Front)", group: "Yoga", positions: 3, emoji: "🧘" },
  // Yoga static hold — entry, hold, release (mirrors SetFinder_YogaCobra)
  { id: "yogaCobraSide", label: "Yoga — Cobra (Side)", group: "Yoga", positions: 3, emoji: "🧘" },
  // Boxing — guard, jab, cross, recover (mirrors SetFinder_Boxing)
  { id: "boxingJabCrossSide",  label: "Boxing — Jab-Cross (Side)",  group: "Boxing", positions: 4, emoji: "🥊" },
  { id: "boxingJabCrossFront", label: "Boxing — Jab-Cross (Front)", group: "Boxing", positions: 4, emoji: "🥊" },
  // Yoga static hold — entry, hold, release (mirrors SetFinder_YogaPlank)
  { id: "yogaPlankSide", label: "Yoga — Plank (Side)", group: "Yoga", positions: 3, emoji: "🧘" },
  // Yoga static holds — entry, hold, release (mirrors SetFinder_YogaChair)
  { id: "yogaChairSide",  label: "Yoga — Chair (Side)",  group: "Yoga", positions: 3, emoji: "🧘" },
  { id: "yogaChairFront", label: "Yoga — Chair (Front)", group: "Yoga", positions: 3, emoji: "🧘" },
  // Basketball free throw — set-up, dip, release, follow-through (mirrors SetFinder_FreeThrow)
  { id: "basketballFreeThrowSide",  label: "Basketball — Free Throw (Side)",  group: "Basketball", positions: 4, emoji: "🏀" },
  { id: "basketballFreeThrowFront", label: "Basketball — Free Throw (Front)", group: "Basketball", positions: 4, emoji: "🏀" },
  { id: "yogaTriangleFront",   label: "Yoga — Triangle (Front)",      group: "Yoga", positions: 3, emoji: "🧘" },
  { id: "yogaTriangleSide",    label: "Yoga — Triangle (Side)",       group: "Yoga", positions: 3, emoji: "🧘" },
  { id: "yogaBridgeSide", label: "Yoga — Bridge (Side)", group: "Yoga", positions: 3, emoji: "🧘" },
  { id: "yogaTreeFront", label: "Yoga — Tree Pose (Front)", group: "Yoga", positions: 3, emoji: "🧘" },
  { id: "yogaTreeSide",  label: "Yoga — Tree Pose (Side)",  group: "Yoga", positions: 3, emoji: "🧘" },
];

export const ACTIVITY_LABEL: Record<ActivityId, string> = {
  ...Object.fromEntries(ACTIVITIES.map(s => [s.id, s.label])),
  unknown: "Unknown",
} as Record<ActivityId, string>;
export const ACTIVITY_EMOJI: Record<ActivityId, string> = {
  ...Object.fromEntries(ACTIVITIES.map(s => [s.id, s.emoji])),
  unknown: "🎬",
} as Record<ActivityId, string>;

export type SessionStatus = "processed" | "processing" | "queued" | "failed";

export type Session = {
  id: string;
  athlete: string;
  athleteId: string;
  // Subject-athlete linkage: WHO is in the video (backend tag, uploader-name fallback
  // server-side). athlete/athleteId above stay the uploader for existing consumers.
  subjectName?: string | null;
  subjectContactId?: number | null;
  orgId?: string;        // org this capture lives under
  activity: ActivityId;
  capturedAt: string;    // ISO
  durationSec: number;
  status: SessionStatus;
  thumb: string;         // emoji placeholder
  score: number | null;  // 0..100 rule-score outcome (ruleScoreOverall); null = not rule-scored yet
  rescorePending?: boolean; // a re-score request is queued and not yet satisfied by a newer render
  videoOrderPending?: boolean; // the video-generation order is Pending: a premium analysis is queued
  risk?: RiskLevel;      // workplace only — RiskAI red/yellow/green triage
  rebaScore?: number;    // workplace only — REBA 1..15
  niosLI?: number;       // workplace only — NIOSH lifting index
  task?: string;         // workplace only — task name (e.g. "Order picking")
  metrics: {
    spineAngle: number;
    hipRotation: number;
    shoulderRotation: number;
    armStraightness: number; // 0..1
    pelvicTilt: number;
    kneeFlexion: number;
  };
  notes?: string;
};

export const SESSIONS: Session[] = [
  { id: "s_8821", athleteId: "a_1", athlete: "Mia Chen",       activity: "golfSwingFaceOn", capturedAt: "2026-05-03T07:42:00Z", durationSec: 6, status: "processed",  thumb: "🏌️", score: 87, metrics: { spineAngle: 32.4, hipRotation: 48.1, shoulderRotation: 92.0, armStraightness: 0.91, pelvicTilt: 8.2,  kneeFlexion: 24.1 }, notes: "Lead arm holding well at P3, slight early extension at P6." },
  { id: "s_8820", athleteId: "a_2", athlete: "Daniel Park",    activity: "tennisServeDTL",  capturedAt: "2026-05-03T07:18:00Z", durationSec: 4, status: "processing", thumb: "🎾", score: 72, metrics: { spineAngle: 18.2, hipRotation: 34.5, shoulderRotation: 88.6, armStraightness: 0.84, pelvicTilt: 6.1,  kneeFlexion: 38.4 } },
  { id: "s_8819", athleteId: "a_3", athlete: "Asha Bello",     activity: "cricketBowlingDtl",  capturedAt: "2026-05-03T06:55:00Z", durationSec: 5, status: "processed",  thumb: "🏏", score: 91, metrics: { spineAngle: 21.7, hipRotation: 56.3, shoulderRotation: 110.4, armStraightness: 0.96, pelvicTilt: 11.4, kneeFlexion: 41.0 }, notes: "Seam-up release angle within target band." },
  { id: "s_8818", athleteId: "a_4", athlete: "Liam O'Donnell", activity: "golfSwingDTL",    capturedAt: "2026-05-02T19:11:00Z", durationSec: 7, status: "processed",  thumb: "🏌️", score: 78, metrics: { spineAngle: 34.1, hipRotation: 51.2, shoulderRotation: 95.7, armStraightness: 0.88, pelvicTilt: 9.0,  kneeFlexion: 22.6 } },
  { id: "s_8817", athleteId: "a_5", athlete: "Yara Haddad",    activity: "tennisBackhand",  capturedAt: "2026-05-02T16:03:00Z", durationSec: 3, status: "queued",     thumb: "🎾", score: 64, metrics: { spineAngle: 22.0, hipRotation: 41.0, shoulderRotation: 81.3, armStraightness: 0.79, pelvicTilt: 4.7,  kneeFlexion: 33.2 } },
  { id: "s_8816", athleteId: "a_6", athlete: "Noah Becker",    activity: "bowling",         capturedAt: "2026-05-02T14:47:00Z", durationSec: 5, status: "failed",     thumb: "🎳", score: 0,  metrics: { spineAngle: 12.4, hipRotation: 28.9, shoulderRotation: 64.1, armStraightness: 0.72, pelvicTilt: 3.4,  kneeFlexion: 27.8 } },
  { id: "s_8815", athleteId: "a_1", athlete: "Mia Chen",       activity: "golfSwingFaceOn", capturedAt: "2026-05-02T11:22:00Z", durationSec: 6, status: "processed",  thumb: "🏌️", score: 84, metrics: { spineAngle: 31.8, hipRotation: 47.2, shoulderRotation: 90.9, armStraightness: 0.90, pelvicTilt: 7.9,  kneeFlexion: 23.4 } },
  { id: "s_8814", athleteId: "a_7", athlete: "Tom Akinyemi",   activity: "cricketBatting",  capturedAt: "2026-05-01T18:30:00Z", durationSec: 4, status: "processed",  thumb: "🏏", score: 73, metrics: { spineAngle: 19.6, hipRotation: 38.4, shoulderRotation: 76.2, armStraightness: 0.83, pelvicTilt: 5.9,  kneeFlexion: 36.1 } },
  { id: "s_8813", athleteId: "a_2", athlete: "Daniel Park",    activity: "runGait",         capturedAt: "2026-05-01T08:14:00Z", durationSec: 12, status: "processed", thumb: "🏃", score: 88, metrics: { spineAngle: 6.4, hipRotation: 42.0, shoulderRotation: 28.0, armStraightness: 0.94, pelvicTilt: 7.2, kneeFlexion: 47.5 }, notes: "Cadence 178 spm; vertical oscillation 7.6 cm." },
  { id: "s_8812", athleteId: "a_8", athlete: "Sara Lindqvist", activity: "verticalJump",    capturedAt: "2026-04-30T10:02:00Z", durationSec: 2, status: "processed",  thumb: "🤾", score: 81, metrics: { spineAngle: 24.1, hipRotation: 14.0, shoulderRotation: 32.0, armStraightness: 0.86, pelvicTilt: 9.8, kneeFlexion: 88.2 } },
  { id: "s_8811", athleteId: "a_4", athlete: "Liam O'Donnell", activity: "squatLift",       capturedAt: "2026-04-30T09:30:00Z", durationSec: 5, status: "processed",  thumb: "🏋️", score: 76, metrics: { spineAngle: 41.0, hipRotation: 8.0, shoulderRotation: 12.0, armStraightness: 0.91, pelvicTilt: 6.3, kneeFlexion: 102.4 } },

  // Workplace task assessments (Acme Logistics)
  { id: "s_9001", athleteId: "e_1", athlete: "Frank Reyes",     orgId: "o_5", activity: "wpLift",       capturedAt: "2026-05-03T08:14:00Z", durationSec: 6, status: "processed",  thumb: "📦", score: 62, risk: "yellow", rebaScore: 7,  niosLI: 1.4, task: "Order picking — small parcels",   metrics: { spineAngle: 42.1, hipRotation: 12.0, shoulderRotation: 18.0, armStraightness: 0.84, pelvicTilt: 11.0, kneeFlexion: 78.0 }, notes: "Spine flexion peaks at L3 — recommend bench-height re-stack." },
  { id: "s_9002", athleteId: "e_2", athlete: "Aisha Mensah",    orgId: "o_5", activity: "wpRepetitive", capturedAt: "2026-05-03T07:45:00Z", durationSec: 22, status: "processed", thumb: "🔧", score: 78, risk: "yellow", rebaScore: 5,                  task: "Cable harness assembly",          metrics: { spineAngle: 18.3, hipRotation: 4.0,  shoulderRotation: 38.0, armStraightness: 0.81, pelvicTilt: 5.0,  kneeFlexion: 14.0 }, notes: "Wrist deviation borderline — investigate jig height." },
  { id: "s_9003", athleteId: "e_3", athlete: "Marcus Hill",     orgId: "o_5", activity: "wpOverhead",   capturedAt: "2026-05-03T07:02:00Z", durationSec: 5, status: "processed",  thumb: "🙆", score: 41, risk: "red",    rebaScore: 11,                 task: "Stocking top-shelf SKUs",         metrics: { spineAngle: 14.2, hipRotation: 6.0,  shoulderRotation: 154.0, armStraightness: 0.78, pelvicTilt: 3.0,  kneeFlexion: 12.0 }, notes: "Shoulder elevation >150° — immediate ladder/step-stool intervention." },
  { id: "s_9004", athleteId: "e_4", athlete: "Priya Iyer",      orgId: "o_5", activity: "wpPushPull",   capturedAt: "2026-05-02T18:11:00Z", durationSec: 14, status: "processed", thumb: "🛒", score: 84, risk: "green",  rebaScore: 3,                  task: "Cart push between docks",         metrics: { spineAngle: 10.4, hipRotation: 4.0,  shoulderRotation: 22.0, armStraightness: 0.92, pelvicTilt: 4.0,  kneeFlexion: 20.0 } },
  { id: "s_9005", athleteId: "e_5", athlete: "Jonas Berg",      orgId: "o_5", activity: "wpLift",       capturedAt: "2026-05-02T16:30:00Z", durationSec: 5, status: "processed",  thumb: "📦", score: 70, risk: "yellow", rebaScore: 6,  niosLI: 1.1, task: "Carton transfer to pallet",       metrics: { spineAngle: 36.0, hipRotation: 10.0, shoulderRotation: 16.0, armStraightness: 0.86, pelvicTilt: 9.0,  kneeFlexion: 72.0 } },
  { id: "s_9006", athleteId: "e_1", athlete: "Frank Reyes",     orgId: "o_5", activity: "wpLadder",     capturedAt: "2026-05-02T11:18:00Z", durationSec: 7, status: "processed",  thumb: "🪜", score: 55, risk: "yellow", rebaScore: 8,                  task: "Mezzanine inspection ladder",     metrics: { spineAngle: 22.0, hipRotation: 8.0,  shoulderRotation: 30.0, armStraightness: 0.82, pelvicTilt: 7.0,  kneeFlexion: 40.0 } },
];

// Per-sport phase definitions. Golf uses P1..P10 (SetFinder); other sports have their own
// named phase sets.
export type ActivityPhase = { id: string; label: string; pct: number; cue?: string };

export const ACTIVITY_PHASES: Record<ActivityId, ActivityPhase[]> = {
  volleyballServeSide: [
    { id: "P1", label: "Toss",           pct: 0,   cue: "A long tossing arm, ball in front of the hitting shoulder" },
    { id: "P2", label: "Load",           pct: 35,  cue: "Elbow high and drawn back, shoulders square" },
    { id: "P3", label: "Contact",        pct: 70,  cue: "Full reach above the shoulder" },
    { id: "P4", label: "Follow-through", pct: 100, cue: "Balanced landing, ready for the next ball" },
  ],
  volleyballServeFront: [
    { id: "P1", label: "Toss",           pct: 0,   cue: "Shoulders square to the target" },
    { id: "P2", label: "Load",           pct: 35,  cue: "Shoulders staying level through the load" },
    { id: "P3", label: "Contact",        pct: 70,  cue: "Full reach above the shoulder" },
    { id: "P4", label: "Follow-through", pct: 100, cue: "Landing square and balanced" },
  ],
  baseballPitchSide: [
    { id: "P1", label: "Leg lift",       pct: 0,   cue: "Balanced over the post leg" },
    { id: "P2", label: "Cocked",         pct: 35,  cue: "Staying closed through the load" },
    { id: "P3", label: "Release",        pct: 70,  cue: "Extension out in front" },
    { id: "P4", label: "Follow-through", pct: 100, cue: "Finishing balanced and ready to field" },
  ],
  baseballPitchFront: [
    { id: "P1", label: "Leg lift",       pct: 0,   cue: "Stacked over the post leg" },
    { id: "P2", label: "Cocked",         pct: 35,  cue: "Shoulders square, not flying open" },
    { id: "P3", label: "Release",        pct: 70,  cue: "Extension out in front" },
    { id: "P4", label: "Follow-through", pct: 100, cue: "Finishing square" },
  ],
  deadliftSide: [
    { id: "P1", label: "Bottom",  pct: 0,   cue: "Flat back, shins upright over the midfoot" },
    { id: "P2", label: "Drive",   pct: 50,  cue: "Chest and hips rising together" },
    { id: "P3", label: "Lockout", pct: 100, cue: "Knees and hips finished, standing tall" },
  ],
  deadliftFront: [
    { id: "P1", label: "Bottom",  pct: 0,   cue: "Hips square before the bar moves" },
    { id: "P2", label: "Drive",   pct: 50,  cue: "Both legs sharing the load" },
    { id: "P3", label: "Lockout", pct: 100, cue: "Finishing square" },
  ],
  unknown: [],
  // Football set-pieces — shared phase set, see constants/football.ts
  [FOOTBALL_ACTIVITIES.penalty]: FOOTBALL_PHASES,
  [FOOTBALL_ACTIVITIES.freeKick]: FOOTBALL_PHASES,
  [FOOTBALL_ACTIVITIES.corner]: FOOTBALL_PHASES,
  // Yoga Warrior II — the three phases of a static hold (mirrors SetFinder_Yoga's GetPositionLabel)
  yogaWarrior2Front: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall, stacked over the base" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Front knee bent, back leg straight, arms level" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  // Warrior II from the side — the same hold, graded on what only this camera sees
  yogaWarrior2Side: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Trunk upright over the hips" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Front knee over the ankle, trunk upright" },
    { id: "P3", label: "Release", pct: 100, cue: "Trunk upright standing back up" },
  ],
  // Yoga Downward Dog — the three phases of a static hold (mirrors SetFinder_YogaDownwardDog's GetPositionLabel)
  yogaDownwardDogSide: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Legs and arms straight, hips high, one long line" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  yogaDownwardDogFront: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Hands shoulder-width, feet hip-width, hips level" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  // Yoga Cobra — the three phases of a static hold (mirrors SetFinder_YogaCobra's GetPositionLabel)
  yogaCobraSide: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Lying flat along the mat" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Chest lifted, hands under the shoulders, hips down" },
    { id: "P3", label: "Release", pct: 100, cue: "Settled back down on the mat" },
  ],
  // Boxing jab-cross — the four phases of the combination (mirrors SetFinder_Boxing's GetPositionLabel)
  boxingJabCrossSide: [
    { id: "P1", label: "Guard",   pct: 0,   cue: "Both hands at the chin" },
    { id: "P2", label: "Jab",     pct: 33,  cue: "Lead hand extends, other hand home, head back" },
    { id: "P3", label: "Cross",   pct: 66,  cue: "Rear hand extends, other hand home, head back" },
    { id: "P4", label: "Recover", pct: 100, cue: "Both hands back at the chin" },
  ],
  // The same four phases from the camera ahead of the boxer: this angle grades the punch LINE, which is the
  // cue only a camera down the punch's line can see, rather than the extension the side view grades.
  boxingJabCrossFront: [
    { id: "P1", label: "Guard",   pct: 0,   cue: "Both hands at the chin" },
    { id: "P2", label: "Jab",     pct: 33,  cue: "Lead hand straight down the pipe, other hand home" },
    { id: "P3", label: "Cross",   pct: 66,  cue: "Rear hand straight down the pipe, other hand home" },
    { id: "P4", label: "Recover", pct: 100, cue: "Both hands back at the chin" },
  ],
  // Yoga Plank — the three phases of a static hold (mirrors SetFinder_YogaPlank's GetPositionLabel)
  yogaPlankSide: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall before you come down" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "One straight line, legs straight, shoulders over the hands" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  // Yoga Chair pose — the three phases of a static hold (mirrors SetFinder_YogaChair's GetPositionLabel)
  yogaChairSide: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall over the feet" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Knees bent, hips back, arms in line with the trunk" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  yogaChairFront: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall and even over both feet" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Knees over the feet, pelvis level, arms parallel" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  // Basketball free throw — the four phases of the shot (mirrors SetFinder_FreeThrow's GetPositionLabel)
  basketballFreeThrowSide: [
    { id: "P1", label: "Set-up",         pct: 0,   cue: "Balanced at the line, shoulders over the hips" },
    { id: "P2", label: "Dip",            pct: 35,  cue: "Knees bent — the shot starts from the legs" },
    { id: "P3", label: "Release",        pct: 70,  cue: "Shooting elbow extended through the ball" },
    { id: "P4", label: "Follow-through", pct: 100, cue: "Finish held with the hand up" },
  ],
  basketballFreeThrowFront: [
    { id: "P1", label: "Set-up",         pct: 0,   cue: "Feet about shoulder-width apart" },
    { id: "P2", label: "Dip",            pct: 35,  cue: "Shoulders level and square through the load" },
    { id: "P3", label: "Release",        pct: 70,  cue: "Shooting elbow on the line of the shot" },
    { id: "P4", label: "Follow-through", pct: 100, cue: "Hand finishing over the shooting shoulder" },
  ],
  // Triangle — the four cues the hold grades (mirrors SetFinder_Yoga_Triangle_Front's P2 overlays).
  yogaTriangleFront: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Legs straight, arms in one line, shoulders stacked, stance wide" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  // Triangle from the side — the three DEPTH cues the hold grades, which the front camera cannot see
  // (mirrors SetFinder_Yoga_Triangle_Side's P2 overlays).
  yogaTriangleSide: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Chest open, trunk sideways not forward, front knee tracking" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  // Yoga Bridge — the three phases of a supine static hold (mirrors SetFinder_YogaBridge's GetPositionLabel)
  yogaBridgeSide: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Lying flat, the pelvis on the mat" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Hips lifted, knees over the feet, shoulders down" },
    { id: "P3", label: "Release", pct: 100, cue: "Pelvis back down to the mat" },
  ],
  // Yoga Tree pose — the three phases of a static hold (mirrors SetFinder_YogaTree's GetPositionLabel)
  yogaTreeFront: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Standing tall" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Knee opened out, hands overhead, standing leg straight" },
    { id: "P3", label: "Release", pct: 100, cue: "Back to standing tall" },
  ],
  yogaTreeSide: [
    { id: "P1", label: "Entry",   pct: 0,   cue: "Trunk upright" },
    { id: "P2", label: "Hold",    pct: 50,  cue: "Foot at the inner thigh, trunk upright" },
    { id: "P3", label: "Release", pct: 100, cue: "Trunk upright standing back down" },
  ],
  golfSwingFaceOn: [
    { id: "P1",  label: "Address",                pct: 0,   cue: "Neutral spine, soft knees" },
    { id: "P2",  label: "Takeaway",               pct: 12,  cue: "Triangle intact" },
    { id: "P3",  label: "Lead arm parallel",      pct: 24,  cue: "Lead arm holds straight" },
    { id: "P4",  label: "Top of backswing",       pct: 38,  cue: "Flat lead wrist" },
    { id: "P5",  label: "Transition",             pct: 47,  cue: "Lower body leads" },
    { id: "P6",  label: "Shaft parallel down",    pct: 58,  cue: "Trail elbow tucked" },
    { id: "P7",  label: "Impact",                 pct: 66,  cue: "Hands ahead, shaft forward" },
    { id: "P8",  label: "Release",                pct: 76,  cue: "Connection through release" },
    { id: "P9",  label: "Lead arm parallel post", pct: 86,  cue: "Smooth post-impact rotation" },
    { id: "P10", label: "Finish",                 pct: 100, cue: "Balanced finish, weight on lead foot" },
  ],
  golfSwingDTL: [
    { id: "P1",  label: "Address",                pct: 0 },
    { id: "P2",  label: "Takeaway",               pct: 12 },
    { id: "P3",  label: "Lead arm parallel",      pct: 24 },
    { id: "P4",  label: "Top of backswing",       pct: 38 },
    { id: "P5",  label: "Transition",             pct: 47 },
    { id: "P6",  label: "Shaft parallel down",    pct: 58 },
    { id: "P7",  label: "Impact",                 pct: 66 },
    { id: "P8",  label: "Release",                pct: 76 },
    { id: "P9",  label: "Lead arm parallel post", pct: 86 },
    { id: "P10", label: "Finish",                 pct: 100 },
  ],
  golfPuttingFaceOn: [
    { id: "P1", label: "Address",        pct: 0,   cue: "Eyes over the ball, weight centred" },
    { id: "P2", label: "Takeaway",       pct: 24,  cue: "Quiet wrists, shoulders rock" },
    { id: "P3", label: "Backstroke end", pct: 44,  cue: "Length matched to putt distance" },
    { id: "P4", label: "Impact",         pct: 62,  cue: "Square face, ball struck on the up" },
    { id: "P5", label: "Through-stroke", pct: 80,  cue: "Accelerate smoothly through" },
    { id: "P6", label: "Finish",         pct: 100, cue: "Hold, face stays square to line" },
  ],
  golfPuttingDTL: [
    { id: "P1", label: "Address",        pct: 0 },
    { id: "P2", label: "Takeaway",       pct: 24 },
    { id: "P3", label: "Backstroke end", pct: 44 },
    { id: "P4", label: "Impact",         pct: 62 },
    { id: "P5", label: "Through-stroke", pct: 80 },
    { id: "P6", label: "Finish",         pct: 100 },
  ],
  tennisServeFaceOn: [
    { id: "S1", label: "Stance",         pct: 0,   cue: "Balanced, weight on back foot" },
    { id: "S2", label: "Toss",           pct: 18,  cue: "High and slightly in front" },
    { id: "S3", label: "Trophy pose",    pct: 35,  cue: "Coiled, hold 2s" },
    { id: "S4", label: "Cocking",        pct: 52 },
    { id: "S5", label: "Acceleration",   pct: 68 },
    { id: "S6", label: "Contact",        pct: 80,  cue: "Full extension at contact" },
    { id: "S7", label: "Follow-through", pct: 100 },
  ],
  tennisServeDTL: [
    { id: "S1", label: "Stance",         pct: 0 },
    { id: "S2", label: "Toss",           pct: 18 },
    { id: "S3", label: "Trophy pose",    pct: 35 },
    { id: "S4", label: "Cocking",        pct: 52 },
    { id: "S5", label: "Acceleration",   pct: 68 },
    { id: "S6", label: "Contact",        pct: 80 },
    { id: "S7", label: "Follow-through", pct: 100 },
  ],
  tennisForehand: [
    { id: "F1", label: "Setup",          pct: 0 },
    { id: "F2", label: "Unit turn",      pct: 28,  cue: "Shoulders coil, racket back" },
    { id: "F3", label: "Contact",        pct: 65,  cue: "Contact out in front" },
    { id: "F4", label: "Follow-through", pct: 100 },
  ],
  tennisBackhand: [
    { id: "B1", label: "Setup",          pct: 0 },
    { id: "B2", label: "Backswing",      pct: 35 },
    { id: "B3", label: "Contact",        pct: 65,  cue: "Contact in front of hip" },
    { id: "B4", label: "Follow-through", pct: 100 },
  ],
  // The five phases SetFinder_Cricket_Bowling finds, per angle. The old six-entry list named a
  // "Pre-release" phase the pipeline has never had, and ACTIVITIES claimed nine positions for it.
  cricketBowlingDtl: [
    { id: "P1", label: "Run-up",           pct: 0,   cue: "Tall and balanced into the gather" },
    { id: "P2", label: "Load",             pct: 30,  cue: "Front arm pulled high, bowling arm still back" },
    { id: "P3", label: "Front-foot land",  pct: 55,  cue: "Braced front leg under the hips" },
    { id: "P4", label: "Release",          pct: 80,  cue: "Bowling hand high at the top of the arc" },
    { id: "P5", label: "Follow-through",   pct: 100, cue: "Bowling arm swung down across the body" },
  ],
  cricketBowlingFront: [
    { id: "P1", label: "Run-up",           pct: 0,   cue: "Tall and balanced into the gather" },
    { id: "P2", label: "Load",             pct: 30,  cue: "Front arm pulled high, bowling arm still back" },
    { id: "P3", label: "Front-foot land",  pct: 55,  cue: "Braced front leg under the hips" },
    { id: "P4", label: "Release",          pct: 80,  cue: "Bowling hand high at the top of the arc" },
    { id: "P5", label: "Follow-through",   pct: 100, cue: "Bowling arm swung down across the body" },
  ],
  // The five phases GetCricketBattingPositions actually returns, in its order: the scaffold's
  // six labels named a "Trigger" and a "Backswing" that no detector picks, and ACTIVITIES
  // claimed seven. The C# is the source of truth, so the viewer draws what the analysis returns.
  cricketBatting: [
    { id: "B1", label: "Stance",          pct: 0 },
    { id: "B2", label: "Top of backlift", pct: 30 },
    { id: "B3", label: "Downswing",       pct: 60 },
    { id: "B4", label: "Impact",          pct: 80 },
    { id: "B5", label: "Follow-through",  pct: 100 },
  ],
  bowling: [
    { id: "B1", label: "Address",        pct: 0 },
    { id: "B2", label: "Approach",       pct: 30 },
    { id: "B3", label: "Slide",          pct: 65 },
    { id: "B4", label: "Release",        pct: 80, cue: "Tight release angle" },
    { id: "B5", label: "Follow-through", pct: 100 },
  ],
  runGait: [
    { id: "G1", label: "Initial contact",   pct: 0 },
    { id: "G2", label: "Loading response",  pct: 12 },
    { id: "G3", label: "Mid-stance",        pct: 25 },
    { id: "G4", label: "Terminal stance",   pct: 38 },
    { id: "G5", label: "Pre-swing",         pct: 50 },
    { id: "G6", label: "Initial swing",     pct: 62 },
    { id: "G7", label: "Mid-swing",         pct: 78 },
    { id: "G8", label: "Terminal swing",    pct: 100 },
  ],
  verticalJump: [
    { id: "J1", label: "Stance",          pct: 0 },
    { id: "J2", label: "Countermovement", pct: 25, cue: "Quiet, equal foot pressure" },
    { id: "J3", label: "Propulsion",      pct: 50 },
    { id: "J4", label: "Flight",          pct: 75 },
    { id: "J5", label: "Landing",         pct: 100 },
  ],
  // Squat — the three phases of a rep (mirrors SetFinder_Squat_Side's GetPositionLabel); P1 repeats per rep
  verticalJumpFront: [
    { id: "J1", label: "Stance",          pct: 0,   cue: "Feet about shoulder width" },
    { id: "J2", label: "Countermovement", pct: 25,  cue: "Knees out over the feet" },
    { id: "J3", label: "Propulsion",      pct: 50,  cue: "Both arms up together" },
    { id: "J4", label: "Flight",          pct: 75 },
    { id: "J5", label: "Landing",         pct: 100, cue: "Hips level, knees out" },
  ],
  squatLift: [
    { id: "P1", label: "Bottom",  pct: 0,   cue: "Hips to knee level, chest up" },
    { id: "P2", label: "Drive",   pct: 50,  cue: "Shoulders over the midfoot" },
    { id: "P3", label: "Lockout", pct: 100, cue: "Knees locked out, standing tall" },
  ],
  // The same rep from the front and from behind, graded on what only those cameras see
  squatFront: [
    { id: "P1", label: "Bottom",  pct: 0,   cue: "Shoulder-width stance, knees out over the feet" },
    { id: "P2", label: "Drive",   pct: 50,  cue: "Knees still tracking over the feet" },
    { id: "P3", label: "Lockout", pct: 100, cue: "Hips and shoulders level" },
  ],
  squatBehind: [
    { id: "P1", label: "Bottom",  pct: 0,   cue: "Pelvis level, spine centred" },
    { id: "P2", label: "Drive",   pct: 50,  cue: "Spine centred over the hips" },
    { id: "P3", label: "Lockout", pct: 100, cue: "Pelvis level at the top" },
  ],
  burpeeSide: [
    { id: "P1", label: "Plank", pct: 0,   cue: "Shoulders, hips and heels in one line; hands under the shoulders" },
    { id: "P2", label: "Tuck",  pct: 50,  cue: "Feet jumped back under the hips" },
    { id: "P3", label: "Jump",  pct: 100, cue: "Hips and knees fully extended, arms overhead" },
  ],
  // The same rep from the front and from the side, graded on what only each camera sees
  pullUpFront: [
    { id: "P1", label: "Top",       pct: 0,   cue: "Chin over the bar, shoulders level" },
    { id: "P2", label: "Descent",   pct: 50,  cue: "Both arms sharing the lowering" },
    { id: "P3", label: "Dead hang", pct: 100, cue: "Elbows straight, hands just outside the shoulders" },
  ],
  pullUpSide: [
    { id: "P1", label: "Top",       pct: 0,   cue: "Body straight under the bar — no kip" },
    { id: "P2", label: "Descent",   pct: 50,  cue: "Shoulders staying under the hands" },
    { id: "P3", label: "Dead hang", pct: 100, cue: "Hanging still, legs under the hips" },
  ],
  // Lunge — the three phases of a rep (mirrors SetFinder_Lunge_Side's GetPositionLabel); P1 repeats per rep
  lungeSide: [
    { id: "P1", label: "Bottom",   pct: 0,   cue: "Front thigh to parallel, chest up" },
    { id: "P2", label: "Drive",    pct: 50,  cue: "Torso tall as the hips push back up" },
    { id: "P3", label: "Recovery", pct: 100, cue: "Front knee extended, standing tall" },
  ],
  lungeFront: [
    { id: "P1", label: "Bottom",   pct: 0,   cue: "Knees over the feet, hips level, two tracks" },
    { id: "P2", label: "Drive",    pct: 50,  cue: "Knees still tracking over the feet" },
    { id: "P3", label: "Recovery", pct: 100, cue: "Shoulders level and square" },
  ],
  // The push-up: the same three-phase rep, graded per camera on what only that camera sees
  pushUpSide: [
    { id: "P1", label: "Bottom",  pct: 0,   cue: "Elbow to a right angle, body in one line" },
    { id: "P2", label: "Press",   pct: 50,  cue: "Hold the plank as the chest drives up" },
    { id: "P3", label: "Lockout", pct: 100, cue: "Elbows fully extended at the top" },
  ],
  pushUpFront: [
    { id: "P1", label: "Bottom",  pct: 0,   cue: "Elbows tucked, hands at least shoulder width" },
    { id: "P2", label: "Press",   pct: 50,  cue: "Both arms driving together, shoulders level" },
    { id: "P3", label: "Lockout", pct: 100, cue: "Shoulders and hips square at the top" },
  ],
  wpLift: [
    { id: "L1", label: "Setup",  pct: 0,   cue: "Stable stance, load close to body" },
    { id: "L2", label: "Grip",   pct: 18 },
    { id: "L3", label: "Lift",   pct: 38,  cue: "Hip hinge, neutral spine" },
    { id: "L4", label: "Carry",  pct: 60 },
    { id: "L5", label: "Lower",  pct: 80,  cue: "Mirror the lift, no twist" },
    { id: "L6", label: "Place",  pct: 100 },
  ],
  wpPushPull: [
    { id: "P1", label: "Initial force", pct: 0,   cue: "Straight back, push from legs" },
    { id: "P2", label: "Sustained",     pct: 50 },
    { id: "P3", label: "Stop",          pct: 80 },
    { id: "P4", label: "Release",       pct: 100 },
  ],
  wpOverhead: [
    { id: "O1", label: "Approach", pct: 0 },
    { id: "O2", label: "Reach",    pct: 35,  cue: "Limit shoulder elevation" },
    { id: "O3", label: "Hold",     pct: 70 },
    { id: "O4", label: "Return",   pct: 100 },
  ],
  wpRepetitive: [
    { id: "R1", label: "Cycle start",        pct: 0 },
    { id: "R2", label: "Action peak",        pct: 50,  cue: "Watch wrist deviation" },
    { id: "R3", label: "Cycle end",          pct: 100 },
    { id: "R4", label: "Pause / micro-rest", pct: 100 },
  ],
  wpLadder: [
    { id: "C1", label: "Foot up",   pct: 0 },
    { id: "C2", label: "Hand up",   pct: 30 },
    { id: "C3", label: "Body lift", pct: 60 },
    { id: "C4", label: "Settle",    pct: 100 },
  ],

  // Dance choreographies — each phase is a counted move in the routine.
  // Renegade — the five counted moves the analysis anchors from body pose (mirrors
  // SetFinder_Dance_Renegade's GetPositionLabel). The hand pop, wrist roll and closing pose hold are
  // not anchored yet: the pop is a forward push a front camera cannot see, the roll has no still
  // extreme, and the hold needs a stillness pass.
  danceRenegade: [
    { id: "P1", label: "Woah",           pct: 0,   cue: "Fists stacked together, arms locked" },
    { id: "P2", label: "Brush shoulder", pct: 25,  cue: "Hand up at the opposite shoulder" },
    { id: "P3", label: "Crisscross",     pct: 50,  cue: "Forearms crossed over the chest" },
    { id: "P4", label: "Step + hit",     pct: 75,  cue: "Wide step, hitting arm punched out" },
    { id: "P5", label: "Drop",           pct: 100, cue: "Knees bent into the drop" },
  ],
  // Savage — the five counted moves the analysis anchors from body pose (mirrors
  // SetFinder_Dance_Savage's GetPositionLabel). The body roll and the closing pose hold are not
  // anchored yet: the roll is a travelling wave with no still extreme a front camera can pick out,
  // and the hold needs a stillness pass.
  danceSavage: [
    { id: "P1", label: "Hip drop",       pct: 0,   cue: "One hip driven down on the count" },
    { id: "P2", label: "Side step",      pct: 25,  cue: "Step out wide, stance open" },
    { id: "P3", label: "Hat tip",        pct: 50,  cue: "Hand up to the brow" },
    { id: "P4", label: "Shoulder swag",  pct: 75,  cue: "Shoulder line rocked over" },
    { id: "P5", label: "Spin + cross",   pct: 100, cue: "Forearms crossed over the chest" },
  ],
  // The five moves the technique anchors from body pose, in order (mirrors SetFinder_DanceLevitating's
  // GetPositionLabel). The opening lean back is not here: a lean away from a front camera is foreshortened
  // to almost nothing, so there is no still extreme to anchor a keyframe on.
  danceLevitating: [
    { id: "P1", label: "Float arms",       pct: 0,   cue: "Hands ride up to the shoulder line" },
    { id: "P2", label: "Step out",         pct: 25,  cue: "The step opens the stance" },
    { id: "P3", label: "Hip wave",         pct: 50,  cue: "One hip drives down on the wave" },
    { id: "P4", label: "Spin",             pct: 75,  cue: "Shoulders come all the way round" },
    { id: "P5", label: "Land",             pct: 100, cue: "Both feet down together" },
  ],
  // The four moves a front camera can anchor from body pose alone. The heel-toe slide and the closing
  // pose are deferred: neither has a still extreme in 2D that a keyframe detector can find (see the PR).
  danceToosie: [
    { id: "P1", label: "First slide",    pct: 0,  cue: "Side glide on the beat" },
    { id: "P2", label: "Counter-slide",  pct: 33 },
    { id: "P3", label: "Drop hands",     pct: 66 },
    { id: "P4", label: "Hop",            pct: 100 },
  ],
  // Out West now runs its own pipeline (mirrors SetFinder_Dance_OutWest's GetPositionLabel): the six
  // counted moves a front camera can anchor from body pose. The body roll has no still extreme in 2D and
  // is deferred, so it is no longer listed as a phase the viewer can step to.
  danceOutWest: [
    { id: "P1", label: "Cowboy point",     pct: 0,   cue: "Pointing hand thrown high and wide" },
    { id: "P2", label: "Hat dip",          pct: 20,  cue: "Hand to the brow as the head dips" },
    { id: "P3", label: "Hip switch",       pct: 40,  cue: "One hip dropped clear of the other" },
    { id: "P4", label: "Step kick",        pct: 60,  cue: "Stance open, foot kicked out" },
    { id: "P5", label: "Spin",             pct: 80,  cue: "Shoulders turned through the spin" },
    { id: "P6", label: "Freeze",           pct: 100, cue: "Square to the camera and still" },
  ],
  // WAP — the four counted moves the analysis anchors from body pose (mirrors SetFinder_Dance_Wap's
  // GetPositionLabel). The body roll is not anchored yet: it is a wave travelling through the torso with
  // no still extreme a front camera can pick out.
  danceWap: [
    { id: "P1", label: "Squat drop",     pct: 0,   cue: "Hips driven right down on the count" },
    { id: "P2", label: "Hip pop",        pct: 33,  cue: "Pelvis pushed clear of the shoulder line" },
    { id: "P3", label: "Spin",           pct: 66,  cue: "Shoulders all the way round on the turn" },
    { id: "P4", label: "Pose",           pct: 100, cue: "Hands and feet reaching out, held still" },
  ],
  // Iconic by Mistake — the nine counted moves, named after the lyric each lands on (mirrors
  // SetFinder_Dance_IconicByMistake's GetPositionLabel). All nine are anchored from body pose.
  danceIconicByMistake: [
    { id: "P1", label: "Hating kick",         pct: 0,   cue: "Foot flicked up behind, hands overhead" },
    { id: "P2", label: "Breakfast",           pct: 12,  cue: "Arms thrown wide over a wide stance" },
    { id: "P3", label: "Dinner",              pct: 25,  cue: "Both hands pulled in to the stomach" },
    { id: "P4", label: "Algorithm",           pct: 37,  cue: "Arms sweep out as one knee lifts" },
    { id: "P5", label: "Fingers",             pct: 50,  cue: "Hands dropped low by the hips" },
    { id: "P6", label: "You",                 pct: 62,  cue: "Elbows lifted level with the shoulders" },
    { id: "P7", label: "Comments",            pct: 75,  cue: "Hands together at the chest" },
    { id: "P8", label: "Iconic",              pct: 87,  cue: "Side-on, lead arm reaching up" },
    { id: "P9", label: "Iconic (other side)", pct: 100, cue: "The same pose, turned the other way" },
  ],
  skateOllie: [
    { id: "P1", label: "Set-up",           pct: 0,   cue: "Balanced, knees bent over the board" },
    { id: "P2", label: "Crouch",           pct: 30,  cue: "Load down through the legs" },
    { id: "P3", label: "Pop",              pct: 55,  cue: "Snap the tail, jump up" },
    { id: "P4", label: "Tuck / level",     pct: 75,  cue: "Slide front foot, level the board" },
    { id: "P5", label: "Land",             pct: 100, cue: "Bend knees, absorb over the bolts" },
  ],
  skateKickflip: [
    { id: "P1", label: "Set-up",           pct: 0 },
    { id: "P2", label: "Crouch",           pct: 30 },
    { id: "P3", label: "Pop + flick",      pct: 55, cue: "Flick off the corner" },
    { id: "P4", label: "Catch",            pct: 75, cue: "Catch the board with both feet" },
    { id: "P5", label: "Land",             pct: 100 },
  ],
  skateDropIn: [
    { id: "P1", label: "Tail set",         pct: 0,   cue: "Tail on the coping, weight back" },
    { id: "P2", label: "Commit",           pct: 40,  cue: "Lean shoulders forward over the front foot" },
    { id: "P3", label: "Front wheels down", pct: 70, cue: "Slam the front trucks down" },
    { id: "P4", label: "Ride out",         pct: 100, cue: "Knees bent, weight centred" },
  ],
};

// Legacy alias used by golf-specific surfaces (Phase timeline + per-phase progression).
// Key positions P1..P10 from EL-Golf SetFinder pattern
export const KEY_POSITIONS = [
  { id: "P1",  label: "Address",                pct: 0 },
  { id: "P2",  label: "Takeaway",               pct: 12 },
  { id: "P3",  label: "Lead arm parallel",      pct: 24 },
  { id: "P4",  label: "Top of backswing",       pct: 38 },
  { id: "P5",  label: "Transition",             pct: 47 },
  { id: "P6",  label: "Shaft parallel down",    pct: 58 },
  { id: "P7",  label: "Impact",                 pct: 66 },
  { id: "P8",  label: "Release",                pct: 76 },
  { id: "P9",  label: "Lead arm parallel post", pct: 86 },
  { id: "P10", label: "Finish",                 pct: 100 },
];

// Synthetic per-frame angle data across the swing
export const ANGLE_SERIES = Array.from({ length: 60 }, (_, i) => {
  const t = i / 59;
  return {
    frame: i,
    spine:    32 + 6 * Math.sin(t * Math.PI * 1.2),
    hip:      18 + 32 * Math.sin(t * Math.PI),
    shoulder: 14 + 78 * Math.sin(t * Math.PI),
    elbow:    150 - 40 * Math.sin(t * Math.PI * 1.4),
  };
});

// What kind of relationship this person has with the org. Drives per-person UI
// framing — risk vs performance, "Assign training" vs "Share report", etc.
// Independent of OrgType — a sports academy can hire employees, a workplace
// can have an external coach (client), and so on.
//
// Maps to OlaCore's Org_Contact relationship via `BaseOrgContact.contactType`
// (ENUM_SYS_ContactType: UserMember / Customer / Supplier). Eleetz subdivides
// UserMember into athlete vs employee for analytics framing, and adds
// "friend" + "self" as application-only kinds that don't need a backing
// Org_Contact row.
export type AthleteKind = "athlete" | "client" | "employee" | "friend" | "self";

export const KIND_LABEL: Record<AthleteKind, string> = {
  athlete:  "Athlete",
  client:   "Client",
  employee: "Employee",
  friend:   "Friend",
  self:     "You",
};

export type Athlete = {
  id: string;
  name: string;
  activity: string;            // for employees this is the role / department label
  kind: AthleteKind;
  sessions: number;
  lastSeen: string;
  initials: string;
  trend: number[];          // movement-quality / risk-trend sparkline
  team?: string;
  orgId?: string;           // home org — drives roster scoping
  risk?: RiskLevel;         // employees only — current rolling risk
  shift?: string;           // employees only
};

export const ATHLETES: Athlete[] = [
  { id: "a_1", name: "Mia Chen",        kind: "self",    activity: "Golf",    sessions: 24, lastSeen: "today",  initials: "MC", trend: [62,68,71,74,77,82,84,87], team: "Eleetz Performance" },
  { id: "a_2", name: "Daniel Park",     kind: "athlete", activity: "Tennis",  sessions: 11, lastSeen: "today",  initials: "DP", trend: [70,68,72,74,72,76,78,72], team: "Riverside TC" },
  { id: "a_3", name: "Asha Bello",      kind: "client",  activity: "Cricket", sessions: 18, lastSeen: "today",  initials: "AB", trend: [78,80,82,84,86,88,90,91], team: "MCC Academy" },
  { id: "a_4", name: "Liam O'Donnell",  kind: "athlete", activity: "Golf",    sessions: 32, lastSeen: "1d ago", initials: "LO", trend: [70,72,74,73,75,76,77,78], team: "Eleetz Performance" },
  { id: "a_5", name: "Yara Haddad",     kind: "client",  activity: "Tennis",  sessions: 7,  lastSeen: "1d ago", initials: "YH", trend: [50,54,56,58,60,62,63,64], team: "Riverside TC" },
  { id: "a_6", name: "Noah Becker",     kind: "client",  activity: "Bowling", sessions: 4,  lastSeen: "1d ago", initials: "NB", trend: [40,44,46,48,50,52,55,58], team: "City Lanes" },
  { id: "a_7", name: "Tom Akinyemi",    kind: "athlete", activity: "Cricket", sessions: 14, lastSeen: "2d ago", initials: "TA", trend: [60,62,65,68,70,71,72,73], team: "MCC Academy" },
  { id: "a_8", name: "Sara Lindqvist",  kind: "athlete", activity: "Golf",    sessions: 19, lastSeen: "3d ago", initials: "SL", trend: [55,58,60,64,68,72,76,81], team: "Eleetz Performance" },

  // Acme Logistics employees (Workplace org). Same Athlete entity — just kind=employee.
  { id: "e_1", name: "Frank Reyes",   kind: "employee", activity: "Picker",            sessions: 12, lastSeen: "today",  initials: "FR", trend: [60,58,62,64,62,65,63,62], team: "Bay 3 — Outbound", orgId: "o_5", risk: "yellow", shift: "Day"   },
  { id: "e_2", name: "Aisha Mensah",  kind: "employee", activity: "Assembler",         sessions: 18, lastSeen: "today",  initials: "AM", trend: [70,72,74,75,76,78,77,78], team: "Line 7 — Cable",   orgId: "o_5", risk: "yellow", shift: "Day"   },
  { id: "e_3", name: "Marcus Hill",   kind: "employee", activity: "Stocker",           sessions: 9,  lastSeen: "today",  initials: "MH", trend: [55,52,50,48,46,44,42,41], team: "Aisle A1",         orgId: "o_5", risk: "red",    shift: "Night" },
  { id: "e_4", name: "Priya Iyer",    kind: "employee", activity: "Material handler",  sessions: 21, lastSeen: "1d ago", initials: "PI", trend: [78,80,82,83,84,85,84,84], team: "Dock — North",     orgId: "o_5", risk: "green",  shift: "Day"   },
  { id: "e_5", name: "Jonas Berg",    kind: "employee", activity: "Picker",            sessions: 7,  lastSeen: "1d ago", initials: "JB", trend: [65,67,68,69,70,70,72,70], team: "Bay 5 — Inbound",  orgId: "o_5", risk: "yellow", shift: "Day"   },
];

export type AIReport = {
  id: string;
  athlete: string;
  activity: string;
  generatedAt: string;
  format: "PDF" | "HTML";
  highlights: string[];
};

export const REPORTS: AIReport[] = [
  { id: "r_201", athlete: "Mia Chen",      activity: "Golf — Face-On",   generatedAt: "2026-05-03", format: "PDF",
    highlights: ["+5° lead-arm hold at P3 vs. last session", "Hip-rotation gain at P4 (+3.1°)", "Slight early extension at P6 — drill suggested"] },
  { id: "r_200", athlete: "Asha Bello",    activity: "Cricket — Bowling",generatedAt: "2026-05-03", format: "HTML",
    highlights: ["Front-foot landing 8% wider than baseline", "Release angle within elite band", "Shoulder rotation 110° (target 105–115°)"] },
  { id: "r_199", athlete: "Daniel Park",   activity: "Run — Gait Cycle", generatedAt: "2026-05-01", format: "PDF",
    highlights: ["Cadence up 4 spm vs. prior block", "Reduced contralateral pelvic drop", "Vertical oscillation trending down"] },
  { id: "r_198", athlete: "Sara Lindqvist",activity: "Vertical Jump",    generatedAt: "2026-04-30", format: "HTML",
    highlights: ["Knee flexion at countermovement +6°", "Symmetry index improved 92% → 96%"] },

  // Workplace reports (Acme Logistics)
  { id: "r_w01", athlete: "Marcus Hill",  activity: "Workplace · Overhead reach", generatedAt: "2026-05-03", format: "PDF",
    highlights: ["Shoulder elevation peaked at 154° (>150° threshold)", "REBA score 11 — high risk", "Engineering control: step stool / replan SKU placement"] },
  { id: "r_w02", athlete: "Frank Reyes",  activity: "Workplace · Manual lift",    generatedAt: "2026-05-03", format: "HTML",
    highlights: ["NIOSH lifting index 1.4 — moderate risk", "Spine flexion peaks at L3", "Recommend hip-hinge drill + bench-height re-stack"] },
  { id: "r_w03", athlete: "Aisha Mensah", activity: "Workplace · Repetitive assembly", generatedAt: "2026-05-02", format: "PDF",
    highlights: ["Wrist deviation borderline — investigate jig height", "REBA 5 — moderate", "RULA score 4 (upper-limb scope)"] },
];

export type IntegrationScope = "personal" | "org";

export type Integration = {
  id: string;
  name: string;
  scope: IntegrationScope;
  category: "Wearables" | "Health" | "Social" | "Storage" | "AMS" | "CMS" | "Webhook" | "SSO";
  status: "connected" | "available" | "error";
  description: string;
};

// Personal integrations — for any user, regardless of org plan.
export const PERSONAL_INTEGRATIONS: Integration[] = [
  { id: "p_1", name: "Garmin Connect",    scope: "personal", category: "Wearables", status: "connected", description: "Pull HR, cadence and activity context to enrich each capture." },
  { id: "p_2", name: "Apple Health",      scope: "personal", category: "Health",    status: "available", description: "Read workouts and basal metrics; write back movement-quality scores." },
  { id: "p_3", name: "Strava",            scope: "personal", category: "Social",    status: "available", description: "Cross-post your sessions and PRs to your Strava feed." },
  { id: "p_4", name: "Google Drive",      scope: "personal", category: "Storage",   status: "connected", description: "Auto-archive raw clips into your personal Drive." },
  { id: "p_5", name: "WHOOP",             scope: "personal", category: "Wearables", status: "available", description: "Add recovery + strain context to each capture." },
];

// Organisation integrations — only meaningful inside a paid org.
export const ORG_INTEGRATIONS: Integration[] = [
  { id: "o_i_1", name: "Hudl Sportscode",     scope: "org", category: "AMS",     status: "connected", description: "Sync session metrics + clips into Hudl for video review." },
  { id: "o_i_2", name: "Smartabase",          scope: "org", category: "AMS",     status: "connected", description: "Push body-measure exports into AMS athlete profiles." },
  { id: "o_i_3", name: "Kitman Labs",         scope: "org", category: "AMS",     status: "available", description: "Send daily session JSON to Kitman pipelines." },
  { id: "o_i_4", name: "TrainingPeaks",       scope: "org", category: "AMS",     status: "available", description: "Attach Eleetz reports to athlete training calendars." },
  { id: "o_i_5", name: "S3 (custom bucket)",  scope: "org", category: "Storage", status: "connected", description: "Mirror raw + processed clips into your org's bucket." },
  { id: "o_i_6", name: "Webhook — session.processed", scope: "org", category: "Webhook", status: "connected", description: "POST session metrics on processing completion." },
  { id: "o_i_7", name: "Okta SSO",            scope: "org", category: "SSO",     status: "available", description: "Federate sign-in for every coach and athlete in the org." },
];

// ---------- Explore feed (public sessions) ----------

export type CameraAngle = "Face-on" | "DTL" | "Side" | "Rear";

export type ExploreItem = {
  id: string;
  athlete: string;
  initials: string;
  org: string;
  activity: ActivityId;
  angle: CameraAngle;
  score: number;
  thumb: string;
  capturedAt: string;
  cheers: number;
  views: number;
  caption: string;
};

export const EXPLORE_FEED: ExploreItem[] = [
  { id: "e_1", athlete: "Hiro Tanaka",   initials: "HT", org: "Eleetz Global",       activity: "golfSwingFaceOn", angle: "Face-on", score: 92, thumb: "🏌️", capturedAt: "2026-05-03T08:14:00Z", cheers: 41, views: 312, caption: "Cleanest P3 hold I've had — dialed in the alignment-stick drill." },
  { id: "e_2", athlete: "Priya Shah",    initials: "PS", org: "MCC Academy",         activity: "cricketBowlingDtl",  angle: "Side",    score: 89, thumb: "🏏", capturedAt: "2026-05-03T07:55:00Z", cheers: 28, views: 204, caption: "Front-foot landing into release angle — feedback says elite band." },
  { id: "e_3", athlete: "Carlos Mendes", initials: "CM", org: "Eleetz Global",       activity: "golfSwingDTL",    angle: "DTL",     score: 84, thumb: "🏌️", capturedAt: "2026-05-03T07:21:00Z", cheers: 19, views: 158, caption: "Working on tempo; AI says shaft parallel position is +4° vs. last week." },
  { id: "e_4", athlete: "Anna Kowalski", initials: "AK", org: "Riverside TC",        activity: "tennisServeDTL",  angle: "DTL",     score: 81, thumb: "🎾", capturedAt: "2026-05-03T06:48:00Z", cheers: 22, views: 142, caption: "Trophy pose at contact — knee drive is back." },
  { id: "e_5", athlete: "Jonas Berg",    initials: "JB", org: "Eleetz Global",       activity: "runGait",         angle: "Side",    score: 90, thumb: "🏃", capturedAt: "2026-05-02T19:30:00Z", cheers: 33, views: 261, caption: "Cadence 184, vertical osc down 0.4 cm." },
  { id: "e_6", athlete: "Lily Okafor",   initials: "LO", org: "Eleetz Performance",  activity: "verticalJump",    angle: "Face-on", score: 86, thumb: "🤾", capturedAt: "2026-05-02T18:02:00Z", cheers: 17, views: 121, caption: "Symmetry index 96% — best ever." },
  { id: "e_7", athlete: "Mateo Rossi",   initials: "MR", org: "Eleetz Global",       activity: "golfSwingFaceOn", angle: "Face-on", score: 78, thumb: "🏌️", capturedAt: "2026-05-02T16:11:00Z", cheers: 11, views: 88,  caption: "Big breakthrough on transition — feels effortless." },
  { id: "e_8", athlete: "Sara Nakamura", initials: "SN", org: "Eleetz Performance",  activity: "tennisBackhand",  angle: "Face-on", score: 79, thumb: "🎾", capturedAt: "2026-05-02T14:45:00Z", cheers: 14, views: 109, caption: "Working on contact-point depth." },
  { id: "e_9", athlete: "Diego Alvarez", initials: "DA", org: "City Lanes",          activity: "bowling",         angle: "Rear",    score: 83, thumb: "🎳", capturedAt: "2026-05-02T11:30:00Z", cheers: 9,  views: 67,  caption: "Release angle tight, more curve." },

  // Dance entries — TikTok-trend captures, public + competing
  { id: "e_10", athlete: "Zara Ahmed",   initials: "ZA", org: "Eleetz Global", activity: "danceRenegade",   angle: "Face-on", score: 96, thumb: "💃", capturedAt: "2026-05-03T09:02:00Z", cheers: 184, views: 4218, caption: "First clean run on Renegade — beat-sync 100%." },
  { id: "e_11", athlete: "Kai Chen",     initials: "KC", org: "Eleetz Global", activity: "danceSavage",     angle: "Face-on", score: 91, thumb: "🕺", capturedAt: "2026-05-03T08:30:00Z", cheers: 162, views: 3804, caption: "Hip drop landed exactly on the snare." },
  { id: "e_12", athlete: "Lola Diaz",    initials: "LD", org: "Eleetz Global", activity: "danceLevitating", angle: "Face-on", score: 88, thumb: "💃", capturedAt: "2026-05-03T07:48:00Z", cheers: 121, views: 2950, caption: "Spin transition is clean now — countless reps." },
];

// ---------- TikTok dance trends + global leaderboards ----------

// Generic video reference — embeds + canonical link helpers live in components/VideoEmbed.
export type VideoRef = {
  platform: "youtube" | "tiktok" | "instagram";
  id: string;       // youtube videoId, tiktok numeric video id, instagram shortcode
  handle?: string;  // tiktok / instagram username (used for canonical URL)
};

export type TikTokTrend = {
  id: string;
  activityId: ActivityId;     // links to the dance template in ACTIVITIES
  title: string;              // dance handle ("Renegade")
  song: string;
  artist: string;
  originator: string;         // creator who started the trend
  bpm: number;
  startedAt: string;
  endsAt: string;              // when the leaderboard locks
  plays: number;               // global plays of the trend
  emoji: string;
  hashtag: string;
  referenceVideo?: VideoRef;   // song MV / reference clip — secondary on the trend page
};

export const TRENDS: TikTokTrend[] = [
  { id: "t_1", activityId: "danceRenegade",   title: "Renegade",     song: "Lottery (Renegade)",  artist: "K Camp",              originator: "Jalaiah Harmon",   bpm: 100, startedAt: "2026-04-19", endsAt: "2026-05-10T23:59:00Z", plays: 18_400_000, emoji: "💃", hashtag: "#renegade",            referenceVideo: { platform: "youtube", id: "dVfzYoriPgc" } },
  { id: "t_2", activityId: "danceSavage",     title: "Savage",       song: "Savage",              artist: "Megan Thee Stallion", originator: "Keara Wilson",     bpm: 90,  startedAt: "2026-04-22", endsAt: "2026-05-13T23:59:00Z", plays: 12_900_000, emoji: "🕺", hashtag: "#savagechallenge",     referenceVideo: { platform: "youtube", id: "jbWgHOadr8w" } },
  { id: "t_3", activityId: "danceLevitating", title: "Levitating",   song: "Levitating",          artist: "Dua Lipa",            originator: "@dontomass",       bpm: 103, startedAt: "2026-04-25", endsAt: "2026-05-16T23:59:00Z", plays:  9_750_000, emoji: "💃", hashtag: "#levitatingchallenge", referenceVideo: { platform: "youtube", id: "TUVcZfQe-Kw" } },
  { id: "t_4", activityId: "danceToosie",     title: "Toosie Slide", song: "Toosie Slide",        artist: "Drake",               originator: "@theshiggyshow",   bpm: 81,  startedAt: "2026-04-28", endsAt: "2026-05-19T23:59:00Z", plays:  7_220_000, emoji: "🕺", hashtag: "#toosieslide",         referenceVideo: { platform: "youtube", id: "xWggTb45brM" } },
  { id: "t_5", activityId: "danceOutWest",    title: "Out West",     song: "Out West",            artist: "Travis Scott",        originator: "@bdash_2",         bpm: 132, startedAt: "2026-04-26", endsAt: "2026-05-17T23:59:00Z", plays:  5_410_000, emoji: "💃", hashtag: "#outwestchallenge",    referenceVideo: { platform: "youtube", id: "qAlF70MdfwU" } },
  { id: "t_6", activityId: "danceWap",        title: "WAP",          song: "WAP",                 artist: "Cardi B",             originator: "@brian.esperon",   bpm: 132, startedAt: "2026-04-15", endsAt: "2026-05-06T23:59:00Z", plays:  4_180_000, emoji: "🕺", hashtag: "#wapchallenge",        referenceVideo: { platform: "youtube", id: "hsm4poTWjMs" } },
];

export type LeaderboardEntry = {
  rank: number;
  user: string;
  initials: string;
  country: string;            // ISO 2-letter
  flag: string;               // flag emoji
  score: number;              // 0..100
  views: number;
  thumb: string;
  video?: VideoRef;           // entry's actual video on YouTube / TikTok / Instagram
};

// Synthetic top-10 per trend. Sorted by score descending.
export const TREND_LEADERBOARDS: Record<string, LeaderboardEntry[]> = {
  t_1: [
    { rank: 1,  user: "Charli D'Amelio",  initials: "CD", country: "US", flag: "🇺🇸", score: 99, views: 12_400_000, thumb: "💃",
      video: { platform: "tiktok", id: "7244310750108323115", handle: "chvrlidamvlioraress" } },
    { rank: 2,  user: "Mei Tanaka",       initials: "MT", country: "JP", flag: "🇯🇵", score: 98, views:  9_810_000, thumb: "💃" },
    { rank: 3,  user: "Kim Da-eun",       initials: "KD", country: "KR", flag: "🇰🇷", score: 97, views:  8_220_000, thumb: "💃" },
    { rank: 4,  user: "Sofia Rossi",      initials: "SR", country: "IT", flag: "🇮🇹", score: 96, views:  6_140_000, thumb: "💃" },
    { rank: 5,  user: "Nia Williams",     initials: "NW", country: "US", flag: "🇺🇸", score: 95, views:  5_890_000, thumb: "💃" },
    { rank: 6,  user: "Lara Schmidt",     initials: "LS", country: "DE", flag: "🇩🇪", score: 94, views:  4_730_000, thumb: "💃" },
    { rank: 7,  user: "Camila Vega",      initials: "CV", country: "MX", flag: "🇲🇽", score: 93, views:  4_010_000, thumb: "💃" },
    { rank: 8,  user: "Ava Park",         initials: "AP", country: "AU", flag: "🇦🇺", score: 92, views:  3_280_000, thumb: "💃" },
    { rank: 9,  user: "Noor Salim",       initials: "NS", country: "AE", flag: "🇦🇪", score: 91, views:  2_970_000, thumb: "💃" },
    { rank: 10, user: "Mia Chen",         initials: "MC", country: "GB", flag: "🇬🇧", score: 90, views:  2_410_000, thumb: "💃" },
  ],
  t_2: [
    { rank: 1,  user: "Megan Thee Stallion", initials: "MS", country: "US", flag: "🇺🇸", score: 99, views: 8_920_000, thumb: "🕺",
      video: { platform: "tiktok", id: "6824240810612018438", handle: "theestallion" } },
    { rank: 2,  user: "Keara Wilson",     initials: "KW", country: "US", flag: "🇺🇸", score: 97, views:  7_340_000, thumb: "🕺",
      video: { platform: "tiktok", id: "6802722722389576965", handle: "keke.janajah" } },
    { rank: 3,  user: "Tinashe",          initials: "TI", country: "US", flag: "🇺🇸", score: 96, views:  5_840_000, thumb: "🕺",
      video: { platform: "tiktok", id: "6805266493861940486", handle: "tinashe" } },
    { rank: 4,  user: "Olivia Brown",     initials: "OB", country: "US", flag: "🇺🇸", score: 95, views:  5_120_000, thumb: "🕺" },
    { rank: 5,  user: "Aaliyah Ali",      initials: "AA", country: "CA", flag: "🇨🇦", score: 93, views:  4_380_000, thumb: "🕺" },
    { rank: 6,  user: "Eden Botha",       initials: "EB", country: "ZA", flag: "🇿🇦", score: 92, views:  3_910_000, thumb: "🕺" },
    { rank: 7,  user: "Maya Singh",       initials: "MS", country: "IN", flag: "🇮🇳", score: 91, views:  3_220_000, thumb: "🕺" },
    { rank: 8,  user: "Lucas Silva",      initials: "LS", country: "BR", flag: "🇧🇷", score: 90, views:  2_760_000, thumb: "🕺" },
    { rank: 9,  user: "Hana Kim",         initials: "HK", country: "KR", flag: "🇰🇷", score: 89, views:  2_310_000, thumb: "🕺" },
    { rank: 10, user: "Emma Larsen",      initials: "EL", country: "DK", flag: "🇩🇰", score: 88, views:  2_010_000, thumb: "🕺" },
  ],
  t_3: [
    { rank: 1,  user: "HappyKelli",       initials: "HK", country: "US", flag: "🇺🇸", score: 98, views:  6_220_000, thumb: "💃",
      video: { platform: "tiktok", id: "7470961136674917663", handle: "happykelli" } },
    { rank: 2,  user: "Jolie",            initials: "JO", country: "US", flag: "🇺🇸", score: 96, views:  5_180_000, thumb: "💃",
      video: { platform: "tiktok", id: "7389996689777560878", handle: "jolie_j12" } },
    { rank: 3,  user: "Aya Mori",         initials: "AM", country: "JP", flag: "🇯🇵", score: 95, views:  4_410_000, thumb: "💃" },
    { rank: 4,  user: "Indira Roy",       initials: "IR", country: "IN", flag: "🇮🇳", score: 93, views:  3_540_000, thumb: "💃" },
    { rank: 5,  user: "Ines Lopes",       initials: "IL", country: "PT", flag: "🇵🇹", score: 92, views:  3_080_000, thumb: "💃" },
    { rank: 6,  user: "Maya Cohen",       initials: "MC", country: "IL", flag: "🇮🇱", score: 90, views:  2_640_000, thumb: "💃" },
    { rank: 7,  user: "Tasha Petrov",     initials: "TP", country: "PL", flag: "🇵🇱", score: 89, views:  2_270_000, thumb: "💃" },
    { rank: 8,  user: "Sara Nakamura",    initials: "SN", country: "JP", flag: "🇯🇵", score: 88, views:  1_980_000, thumb: "💃" },
    { rank: 9,  user: "Adaeze Okoye",     initials: "AO", country: "NG", flag: "🇳🇬", score: 87, views:  1_710_000, thumb: "💃" },
    { rank: 10, user: "Tess Carter",      initials: "TC", country: "AU", flag: "🇦🇺", score: 86, views:  1_530_000, thumb: "💃" },
  ],
  t_4: [
    { rank: 1,  user: "The Williams Fam", initials: "TW", country: "US", flag: "🇺🇸", score: 96, views:  4_120_000, thumb: "🕺",
      video: { platform: "tiktok", id: "6811567662850739462", handle: "thewilliamsfam_" } },
    { rank: 2,  user: "Aisha Bello",      initials: "AB", country: "NG", flag: "🇳🇬", score: 94, views:  3_280_000, thumb: "🕺" },
    { rank: 3,  user: "Liam Walsh",       initials: "LW", country: "IE", flag: "🇮🇪", score: 92, views:  2_780_000, thumb: "🕺" },
    { rank: 4,  user: "Yara Haddad",      initials: "YH", country: "LB", flag: "🇱🇧", score: 90, views:  2_110_000, thumb: "🕺" },
    { rank: 5,  user: "Nikolai Volkov",   initials: "NV", country: "RU", flag: "🇷🇺", score: 89, views:  1_820_000, thumb: "🕺" },
  ],
  t_5: [
    { rank: 1,  user: "Kylie Jenner",     initials: "KJ", country: "US", flag: "🇺🇸", score: 95, views:  3_220_000, thumb: "💃",
      video: { platform: "tiktok", id: "6821388600593222917", handle: "kyliejenner" } },
    { rank: 2,  user: "Layla Hassan",     initials: "LH", country: "EG", flag: "🇪🇬", score: 94, views:  2_780_000, thumb: "💃" },
    { rank: 3,  user: "Rosa Bianchi",     initials: "RB", country: "IT", flag: "🇮🇹", score: 92, views:  2_320_000, thumb: "💃" },
    { rank: 4,  user: "Jade Tan",         initials: "JT", country: "SG", flag: "🇸🇬", score: 91, views:  1_870_000, thumb: "💃" },
    { rank: 5,  user: "Niamh O'Brien",    initials: "NO", country: "IE", flag: "🇮🇪", score: 90, views:  1_530_000, thumb: "💃" },
  ],
  t_6: [
    { rank: 1,  user: "Brian Esperon",    initials: "BE", country: "US", flag: "🇺🇸", score: 93, views:  2_010_000, thumb: "🕺",
      video: { platform: "tiktok", id: "6859351345795796230", handle: "besperon" } },
    { rank: 2,  user: "Sara Lindqvist",   initials: "SL", country: "SE", flag: "🇸🇪", score: 92, views:  1_810_000, thumb: "🕺" },
    { rank: 3,  user: "Amaka Eze",        initials: "AE", country: "NG", flag: "🇳🇬", score: 90, views:  1_550_000, thumb: "🕺" },
    { rank: 4,  user: "Gabriela Cruz",    initials: "GC", country: "PT", flag: "🇵🇹", score: 89, views:  1_280_000, thumb: "🕺" },
    { rank: 5,  user: "Mariam Diop",      initials: "MD", country: "SN", flag: "🇸🇳", score: 87, views:  1_010_000, thumb: "🕺" },
  ],
};

// Helpers used by Explore + Trend pages
export function trendForActivity(activityId: ActivityId): TikTokTrend | undefined {
  return TRENDS.find((t) => t.activityId === activityId);
}

export function timeRemaining(iso: string, nowMs = Date.now()): string {
  const ms = new Date(iso).getTime() - nowMs;
  if (ms <= 0) return "Closed";
  const d = Math.floor(ms / (24 * 3600 * 1000));
  const h = Math.floor((ms % (24 * 3600 * 1000)) / (3600 * 1000));
  if (d > 1) return `${d}d ${h}h left`;
  return `${Math.floor(ms / (3600 * 1000))}h left`;
}

export function formatPlays(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

// ---------- Library: drills, camera setups, glossary ----------

export type Drill = {
  id: string;
  activity: "Golf" | "Tennis" | "Cricket" | "Bowling" | "Movement" | "Workplace";
  phase?: string;       // P1..P10 for golf; named phases for others
  label: string;
  goal: string;
  cue: string;          // the coaching cue (or safety intervention for workplace)
  duration: string;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
  equipment: string[];
};

export const DRILLS: Drill[] = [
  // Golf — phase-tied
  { id: "d_g1", activity: "Golf", phase: "P1",  label: "Setup mirror check",         goal: "Neutral spine + balanced weight at address", cue: "Belt buckle stays soft; eyes over the ball", duration: "5 min",      difficulty: "Beginner",     equipment: ["Mirror"] },
  { id: "d_g2", activity: "Golf", phase: "P2",  label: "Low-and-slow takeaway",      goal: "One-piece takeaway, no early wrist hinge",   cue: "Triangle stays intact past the trail leg",  duration: "3 sets x 5", difficulty: "Beginner",     equipment: ["Alignment stick"] },
  { id: "d_g3", activity: "Golf", phase: "P3",  label: "Lead-arm hold drill",        goal: "Maintain >170° lead-arm straightness",       cue: "Stick under lead arm — keep it pinned",     duration: "5 min",      difficulty: "Intermediate", equipment: ["Alignment stick"] },
  { id: "d_g4", activity: "Golf", phase: "P4",  label: "Top-of-swing pause",         goal: "Flat lead wrist, tucked trail elbow",        cue: "Pause at the top; mirror check in slow-mo", duration: "5 min",      difficulty: "Intermediate", equipment: ["Mirror"] },
  { id: "d_g5", activity: "Golf", phase: "P5",  label: "Ground reaction transition", goal: "Lower-body lead before upper rotation",      cue: "Push the ground; let the arms drop",        duration: "10 min",     difficulty: "Advanced",     equipment: [] },
  { id: "d_g6", activity: "Golf", phase: "P6",  label: "9-to-3 with pause",          goal: "Reduce early extension at shaft parallel",   cue: "Pause at parallel — feel the squat",        duration: "10 min",     difficulty: "Intermediate", equipment: ["Alignment stick"] },
  { id: "d_g7", activity: "Golf", phase: "P7",  label: "Impact bag",                 goal: "Hands ahead, shaft forward lean",            cue: "Strike the bag — feel forward shaft lean",  duration: "8 min",      difficulty: "Beginner",     equipment: ["Impact bag"] },
  { id: "d_g8", activity: "Golf", phase: "P8",  label: "Towel release drill",        goal: "Connection through release",                 cue: "Towel pinned under both armpits",           duration: "6 min",      difficulty: "Intermediate", equipment: ["Towel"] },
  { id: "d_g9", activity: "Golf", phase: "P9",  label: "Lead-arm folded post",       goal: "Smooth post-impact rotation",                cue: "Trail shoulder finishes pointing at target", duration: "5 min",     difficulty: "Intermediate", equipment: [] },
  { id: "d_g10", activity: "Golf", phase: "P10", label: "Balanced finish hold",       goal: "Hold finish 3s — full hip rotation",         cue: "Belt buckle to target; weight on lead foot", duration: "3 min",     difficulty: "Beginner",     equipment: [] },

  // Tennis
  { id: "d_t1", activity: "Tennis", phase: "Trophy",   label: "Trophy-pose hold",   goal: "Stable platform before contact", cue: "Hold at trophy 2s before swing",       duration: "5 min",      difficulty: "Beginner",     equipment: [] },
  { id: "d_t2", activity: "Tennis", phase: "Contact",  label: "Contact-point ladder", goal: "Consistent contact height + depth", cue: "Ball bounce → racquet at full extension", duration: "10 min", difficulty: "Intermediate", equipment: ["Cones"] },
  { id: "d_t3", activity: "Tennis", phase: "Backhand", label: "Two-handed pivot",   goal: "Hip lead before arm",         cue: "Outside-foot drive then arms",          duration: "8 min",      difficulty: "Intermediate", equipment: [] },

  // Cricket
  { id: "d_c1", activity: "Cricket", phase: "Run-up",        label: "Stride-length sticks",   goal: "Repeatable run-up rhythm",  cue: "Stride landing on each marker",         duration: "10 min",     difficulty: "Beginner",     equipment: ["Sticks"] },
  { id: "d_c2", activity: "Cricket", phase: "Front-foot",    label: "Front-foot landing",     goal: "Aligned, braced front foot", cue: "Land flat, knee soft, hip braced",     duration: "3 sets x 6", difficulty: "Intermediate", equipment: [] },
  { id: "d_c3", activity: "Cricket", phase: "Release",       label: "Wall-touch release",     goal: "High release angle",         cue: "Touch a high wall on follow-through",   duration: "5 min",      difficulty: "Advanced",     equipment: ["Wall"] },

  // Bowling (10-pin)
  { id: "d_b1", activity: "Bowling", phase: "Approach",  label: "4-step approach drill", goal: "Repeatable timing", cue: "Counted footwork 1-2-3-4 to release", duration: "10 min", difficulty: "Beginner",     equipment: [] },
  { id: "d_b2", activity: "Bowling", phase: "Release",   label: "Towel under heel",      goal: "Tight release angle", cue: "Heel stays planted at release",     duration: "5 min",  difficulty: "Intermediate", equipment: ["Towel"] },

  // Movement
  { id: "d_m1", activity: "Movement", phase: "Run",   label: "Cadence ladder 170 → 185", goal: "Raise cadence without losing form", cue: "Metronome at +5 spm intervals", duration: "20 min", difficulty: "Intermediate", equipment: ["Metronome"] },
  { id: "d_m2", activity: "Movement", phase: "Jump",  label: "Box drop + countermovement", goal: "Reactive depth + symmetry",        cue: "Quiet landing — equal foot pressure", duration: "3 sets x 6", difficulty: "Advanced", equipment: ["Box"] },
  { id: "d_m3", activity: "Movement", phase: "Squat", label: "Tempo back squat",            goal: "Depth + neutral spine",            cue: "3-1-1 tempo; brace before descent",   duration: "4 sets x 5", difficulty: "Intermediate", equipment: ["Barbell"] },

  // Workplace — labelled "Drills" for consistency but framed as safety interventions / training.
  { id: "d_w1", activity: "Workplace", phase: "L3", label: "Hip-hinge lift drill",      goal: "Reduce spine flexion at peak load",        cue: "Hinge from hips, knees soft, brace before lift",          duration: "5 min",      difficulty: "Beginner",     equipment: ["Stretch band"] },
  { id: "d_w2", activity: "Workplace", phase: "O2", label: "Ladder + step-stool policy", goal: "Eliminate >150° shoulder elevation",       cue: "Anything above shoulder height needs a step or hoist",   duration: "Toolbox talk", difficulty: "Beginner",   equipment: ["Step stool"] },
  { id: "d_w3", activity: "Workplace", phase: "R2", label: "Wrist-neutral jig",         goal: "Cut wrist deviation in repetitive cycles", cue: "Adjust jig height so wrist stays inside ±15°",          duration: "Engineering control", difficulty: "Intermediate", equipment: ["Workstation re-fit"] },
  { id: "d_w4", activity: "Workplace", phase: "P1", label: "Cart push from legs",       goal: "Lower-back strain on initial force",       cue: "Drive through legs, brace core, push under shoulder",   duration: "5 min",      difficulty: "Beginner",     equipment: [] },
  { id: "d_w5", activity: "Workplace", phase: "C2", label: "Three-point ladder contact", goal: "Reduce slip + balance risk",               cue: "Always two hands + one foot, or two feet + one hand",   duration: "Toolbox talk", difficulty: "Beginner",   equipment: [] },
];

export type CameraSetup = {
  activity: string;
  angle: CameraAngle;
  distanceM: number;
  heightM: number;
  notes: string;
};

export const CAMERA_SETUPS: CameraSetup[] = [
  { activity: "Golf",    angle: "Face-on", distanceM: 2.5, heightM: 1.2, notes: "Camera level with the belt buckle. Frame from feet to head plus 30cm of swing arc." },
  { activity: "Golf",    angle: "DTL",     distanceM: 2.7, heightM: 1.0, notes: "Camera in line with the toe-line, height level with hands at address." },
  { activity: "Tennis",  angle: "Face-on", distanceM: 5.0, heightM: 1.5, notes: "Capture full toss + contact zone. Behind baseline." },
  { activity: "Tennis",  angle: "DTL",     distanceM: 6.0, heightM: 1.5, notes: "Service-line side; frame full kinetic chain." },
  { activity: "Cricket", angle: "Side",    distanceM: 7.0, heightM: 1.4, notes: "Square-of-the-wicket. Frame run-up + release." },
  { activity: "Run",     angle: "Side",    distanceM: 4.0, heightM: 1.0, notes: "Treadmill or 10-stride window; camera at hip height." },
  { activity: "Jump",    angle: "Face-on", distanceM: 3.0, heightM: 1.0, notes: "Frame from above head to floor. 60+ fps recommended." },
  { activity: "Squat",   angle: "Side",    distanceM: 3.0, heightM: 1.0, notes: "Camera at hip height to bar path. Square to the body." },

  // Workplace task setups
  { activity: "Workplace", angle: "Side",    distanceM: 3.5, heightM: 1.2, notes: "Lift / lower tasks — frame from feet to overhead reach. Side-on captures spine angle cleanly." },
  { activity: "Workplace", angle: "Face-on", distanceM: 2.5, heightM: 1.1, notes: "Repetitive assembly + overhead reach — face-on for shoulder elevation + wrist deviation." },
  { activity: "Workplace", angle: "Rear",    distanceM: 4.0, heightM: 1.4, notes: "Push / pull cart tasks — rear-facing captures whole-body alignment under load." },
];

export type GlossaryEntry = {
  term: string;
  definition: string;
  unit?: string;
  appliesTo: string[];
};

export const GLOSSARY: GlossaryEntry[] = [
  { term: "Spine angle",            definition: "Angle of the torso forward of vertical at address and through the swing.",            unit: "°",  appliesTo: ["Golf", "Cricket", "Tennis"] },
  { term: "Hip rotation",           definition: "Rotation of the pelvis relative to the target line, measured at peak backswing and impact.", unit: "°", appliesTo: ["Golf", "Tennis", "Cricket"] },
  { term: "Shoulder rotation",      definition: "Rotation of the upper torso relative to the target line at top of backswing and impact.",   unit: "°", appliesTo: ["Golf", "Tennis", "Cricket"] },
  { term: "Lead-arm straightness",  definition: "Angle of the lead elbow during the backswing; closer to 180° is straighter.",                unit: "°", appliesTo: ["Golf", "Tennis"] },
  { term: "Pelvic tilt",            definition: "Anterior / posterior tilt of the pelvis. Stable through transition is healthy.",             unit: "°", appliesTo: ["Golf", "Run", "Squat"] },
  { term: "Knee flexion",           definition: "Knee bend angle at key positions — countermovement, impact, landing.",                       unit: "°", appliesTo: ["Run", "Jump", "Squat", "Cricket"] },
  { term: "Cadence",                definition: "Steps per minute during running.",                                                            unit: "spm", appliesTo: ["Run"] },
  { term: "Vertical oscillation",   definition: "Vertical movement of the centre of mass per stride.",                                         unit: "cm", appliesTo: ["Run"] },
  { term: "Symmetry index",         definition: "Side-to-side balance metric for jumping and lifting; 100% is perfect symmetry.",              unit: "%", appliesTo: ["Jump", "Squat"] },
  { term: "Release angle",          definition: "Angle of the bowling arm at the moment of ball release.",                                     unit: "°", appliesTo: ["Cricket", "Bowling"] },

  // Workplace ergonomics
  { term: "REBA score",             definition: "Rapid Entire Body Assessment score (1–15). Higher = more risk; >7 means urgent change.",       unit: "1–15", appliesTo: ["Workplace"] },
  { term: "RULA score",             definition: "Rapid Upper Limb Assessment score (1–7). Used for desk + assembly work.",                       unit: "1–7",  appliesTo: ["Workplace"] },
  { term: "NIOSH lifting index",    definition: "Recommended weight limit ratio. LI > 1 means the task exceeds safe limits for most workers.",   unit: "ratio", appliesTo: ["Workplace"] },
  { term: "Lumbar load",            definition: "Compression force at L5/S1 estimated from posture and load.",                                  unit: "N·m", appliesTo: ["Workplace"] },
  { term: "Wrist deviation",        definition: "Angle of wrist away from neutral. Persistent deviation drives MSD risk.",                       unit: "°", appliesTo: ["Workplace"] },
  { term: "Shoulder elevation",     definition: "Angle of upper arm above the body line. >60° sustained, or >150° at peak, increases MSD risk.", unit: "°", appliesTo: ["Workplace"] },
];

export type ShareScope = "private" | "friends" | "org" | "public";
export const SHARE_SCOPES: { id: ShareScope; label: string; hint: string }[] = [
  { id: "private", label: "Only me",    hint: "AI feedback stays in your account" },
  { id: "friends", label: "Friends",    hint: "People you've added on your roster" },
  { id: "org",     label: "Organisation", hint: "Coaches and members of the current org" },
  { id: "public",  label: "Public",     hint: "Surfaces on Explore — visible to anyone" },
];

export type OrgPlan = "global" | "starter" | "pro" | "team";

// Mirror of the OrgType field on Organisation (OlaCore). String-typed so it can
// expand without an enum migration; UI mode is derived from this value.
export type OrgType = "Global" | "Academy" | "Club" | "Clinic" | "Studio" | "Workplace";

export type Org = {
  id: string;
  name: string;
  type: OrgType;
  plan: OrgPlan;
  athletes: number;          // for Workplace orgs this is the employee count
  weeklySessions: number;    // for Workplace orgs this is task assessments / week
  description?: string;
  industry?: string;         // workplace only — manufacturing, logistics, healthcare…
};

export function isWorkplaceOrg(org: Org): boolean {
  return org.type === "Workplace";
}

// Label helpers — words that flip when the active org is Workplace.
export function actorLabel(org: Org, plural = false): string {
  if (isWorkplaceOrg(org)) return plural ? "Employees" : "Employee";
  return plural ? "Athletes" : "Athlete";
}
export function sessionLabel(org: Org, plural = false): string {
  if (isWorkplaceOrg(org)) return plural ? "Task assessments" : "Task assessment";
  return plural ? "Sessions" : "Session";
}
export function templateLabel(org: Org): string {
  return isWorkplaceOrg(org) ? "Task" : "Sport";
}

// Default org. Every user lands here until they pay or are invited elsewhere.
export const GLOBAL_ORG_ID = "o_global";

export const ORGS: Org[] = [
  {
    id: GLOBAL_ORG_ID,
    name: "Eleetz Global",
    type: "Global",
    plan: "global",
    athletes: 12480,
    weeklySessions: 41217,
    description:
      "The shared home for every Eleetz user. Your sessions stay private; the global org is just the lobby until you create or join a paid organisation.",
  },
  { id: "o_1", name: "Eleetz Performance", type: "Academy", plan: "team", athletes: 48, weeklySessions: 132 },
  { id: "o_2", name: "Riverside TC",       type: "Club",    plan: "pro",  athletes: 22, weeklySessions: 47  },
  { id: "o_3", name: "MCC Academy",        type: "Academy", plan: "team", athletes: 31, weeklySessions: 86  },
  { id: "o_4", name: "City Lanes",         type: "Club",    plan: "starter", athletes: 14, weeklySessions: 19 },
  {
    id: "o_5", name: "Acme Logistics", type: "Workplace", plan: "team",
    athletes: 184, weeklySessions: 412,
    industry: "Logistics & warehousing",
    description: "Workplace ergonomics — single-camera task analysis with REBA, RULA and NIOSH-Lifting baked in.",
  },
];

export type OrgRole = "member" | "athlete" | "coach" | "owner";

export const ROLE_LABEL: Record<OrgRole, string> = {
  member:  "Member",
  athlete: "Athlete",
  coach:   "Coach",
  owner:   "Owner",
};

// Memberships for the current user. In a real app this would come from the API.
// "Eleetz Global" is always present; paid orgs are added when invited / subscribed.
// Mia is owner of Eleetz Performance (sport academy) and Acme Logistics
// (workplace) so the demo covers both org types end-to-end.
export const MY_MEMBERSHIPS: { orgId: string; role: OrgRole }[] = [
  { orgId: GLOBAL_ORG_ID,  role: "member" },
  { orgId: "o_1",          role: "owner" },
  { orgId: "o_5",          role: "owner" },
];

export function isOrgManager(role: OrgRole): boolean {
  return role === "owner" || role === "coach";
}

export function planLabel(p: OrgPlan): string {
  return p === "global" ? "Free" : p === "starter" ? "Starter" : p === "pro" ? "Pro" : "Team";
}

export const KPIS = {
  sessionsToday: 27,
  sessionsTrend: [12, 18, 14, 22, 19, 24, 27],
  activeAthletes: 84,
  athletesTrend: [60, 64, 68, 70, 74, 79, 84],
  avgProcessingSec: 27,
  processingTrend: [42, 38, 34, 31, 29, 28, 27],
  insightsGenerated: 312,
  insightsTrend: [180, 210, 235, 260, 280, 295, 312],
};

// ---------- Personal (Strava-style individual mode) ----------

export type AppMode = "personal" | "pro";

export type Me = {
  athleteId: string;
  name: string;
  initials: string;
  primaryActivity: string;
  handicap?: number;
  joinedAt: string;
};

export const ME: Me = {
  athleteId: "a_1",
  name: "Mia Chen",
  initials: "MC",
  primaryActivity: "Golf",
  handicap: 8.4,
  joinedAt: "2026-02-14",
};

// Metric definitions (used by Progress filters and session detail).
export type MetricKey =
  | "score"
  | "spineAngle"
  | "hipRotation"
  | "shoulderRotation"
  | "armStraightness"
  | "pelvicTilt"
  | "kneeFlexion";

export const METRICS: { key: MetricKey; label: string; unit: string; goodHigh: boolean }[] = [
  { key: "score",            label: "Movement quality",     unit: "/100", goodHigh: true  },
  { key: "spineAngle",       label: "Spine angle",          unit: "°",    goodHigh: true  },
  { key: "hipRotation",      label: "Hip rotation",         unit: "°",    goodHigh: true  },
  { key: "shoulderRotation", label: "Shoulder rotation",    unit: "°",    goodHigh: true  },
  { key: "armStraightness",  label: "Lead-arm straightness", unit: "x",   goodHigh: true  },
  { key: "pelvicTilt",       label: "Pelvic tilt",          unit: "°",    goodHigh: false },
  { key: "kneeFlexion",      label: "Knee flexion",         unit: "°",    goodHigh: true  },
];

// Which metrics are relevant per sport family. Used to drive the Progress metric filter.
export const METRICS_BY_FAMILY: Record<string, MetricKey[]> = {
  Golf:      ["score", "spineAngle", "hipRotation", "shoulderRotation", "armStraightness", "pelvicTilt"],
  Tennis:    ["score", "shoulderRotation", "hipRotation", "armStraightness", "kneeFlexion"],
  Cricket:   ["score", "shoulderRotation", "hipRotation", "kneeFlexion"],
  Bowling:   ["score", "shoulderRotation", "armStraightness"],
  Movement:  ["score", "kneeFlexion", "pelvicTilt", "hipRotation"],
  Workplace: ["score", "spineAngle", "kneeFlexion", "shoulderRotation"],
  Dance:     ["score", "shoulderRotation", "hipRotation", "pelvicTilt", "kneeFlexion"],
};

// Workplace ergonomic risk triage (RiskAI-style red/yellow/green).
export type RiskLevel = "green" | "yellow" | "red";
export const RISK_LEVELS: { id: RiskLevel; label: string; hint: string }[] = [
  { id: "green",  label: "Low risk",      hint: "Within safe limits" },
  { id: "yellow", label: "Moderate risk", hint: "Monitor + intervene" },
  { id: "red",    label: "High risk",     hint: "Immediate action" },
];
export function riskClass(r: RiskLevel): { bg: string; text: string; ring: string; dot: string } {
  switch (r) {
    case "green":  return { bg: "bg-el-50",     text: "text-el-700",     ring: "ring-el-200",     dot: "bg-el-500" };
    case "yellow": return { bg: "bg-amber-50",  text: "text-amber-700",  ring: "ring-amber-200",  dot: "bg-amber-500" };
    case "red":    return { bg: "bg-rose-50",   text: "text-rose-700",   ring: "ring-rose-200",   dot: "bg-rose-500" };
  }
}

// Industry-standard ergonomic assessments. The same body-measure stream feeds
// these tools — Eleetz computes the score automatically rather than asking the
// safety manager to answer the questionnaire by hand.
export type ErgoAssessment = {
  id: "REBA" | "RULA" | "NIOSH-LIFT";
  label: string;
  description: string;
  scoreRange: string;
  appliesTo: ActivityId[];
};

export const ERGO_ASSESSMENTS: ErgoAssessment[] = [
  {
    id: "REBA", label: "REBA — Rapid Entire Body Assessment",
    description: "Whole-body posture risk. Common for healthcare, manufacturing, repetitive assembly.",
    scoreRange: "1–15", appliesTo: ["wpLift", "wpRepetitive", "wpOverhead", "wpPushPull"],
  },
  {
    id: "RULA", label: "RULA — Rapid Upper Limb Assessment",
    description: "Upper-body and neck/shoulder posture risk. Best for desk + assembly work.",
    scoreRange: "1–7", appliesTo: ["wpRepetitive", "wpOverhead"],
  },
  {
    id: "NIOSH-LIFT", label: "NIOSH lifting equation",
    description: "Recommended weight limit for two-handed lifting tasks.",
    scoreRange: "Lifting Index (LI) 0–3+", appliesTo: ["wpLift"],
  },
];


// Map a ActivityId to a default camera angle.
export function angleFromActivity(activityId: ActivityId): CameraAngle {
  if (activityId.endsWith("FaceOn"))  return "Face-on";
  if (activityId.endsWith("DTL"))     return "DTL";
  if (activityId === "verticalJumpFront") return "Face-on";
  if (activityId === "verticalJump")  return "Side";   // SportId 23 is the jump's SIDE technique
  if (activityId === "bowling")       return "Rear";
  if (activityId === "squatFront")    return "Face-on";
  if (activityId === "squatBehind")   return "Rear";
  if (activityId === "burpeeSide")    return "Side";
  if (activityId === "pullUpFront")   return "Face-on";
  if (activityId === "pullUpSide")    return "Side";
  if (activityId === "lungeSide")     return "Side";
  if (activityId === "lungeFront")    return "Face-on";
  if (activityId === "pushUpFront")   return "Face-on";
  if (activityId === "wpOverhead" || activityId === "wpRepetitive") return "Face-on";
  if (activityId.startsWith("dance")) return "Face-on";
  if (activityId === "yogaWarrior2Front") return "Face-on";
  if (activityId === "yogaDownwardDogFront") return "Face-on";
  if (activityId === "boxingJabCrossFront") return "Face-on";
  if (activityId === "yogaChairFront") return "Face-on";
  if (activityId === "yogaTriangleFront") return "Face-on";
  if (activityId === "yogaTreeFront") return "Face-on";
  return "Side";
}

// Workplace task helpers
export function isWorkplaceActivity(activityId: ActivityId): boolean {
  return activityId.startsWith("wp");
}

// Sport family for grouping (Golf, Tennis, Cricket, Bowling, Movement).
export function activityFamily(activityId: ActivityId): string {
  return ACTIVITIES.find((s) => s.id === activityId)?.group ?? "Movement";
}

// 12 weekly points per metric. Synthesised so the Progress chart has something to show
// across the metric filter without dragging in heavy chart libraries.
function trend(min: number, max: number, slope: number): { wk: string; value: number }[] {
  return Array.from({ length: 12 }, (_, i) => {
    const t = i / 11;
    const noise = Math.sin(i * 1.7) * (max - min) * 0.04;
    const v = min + (max - min) * (slope > 0 ? t : 1 - t) + noise;
    return { wk: `W-${12 - i}`, value: +v.toFixed(2) };
  });
}

export const MY_METRIC_TRENDS: Record<MetricKey, { wk: string; value: number }[]> = {
  score:            trend(64, 87, +1),
  spineAngle:       trend(28, 33, +1),
  hipRotation:      trend(38, 51, +1),
  shoulderRotation: trend(78, 93, +1),
  armStraightness:  trend(0.78, 0.96, +1),
  pelvicTilt:       trend(11, 7,  -1),  // less is better
  kneeFlexion:      trend(18, 26, +1),
};

// 12-week movement-quality trend (per week, golf primary)
export const MY_TREND = [
  { wk: "W-12", score: 64 },
  { wk: "W-11", score: 66 },
  { wk: "W-10", score: 68 },
  { wk: "W-9",  score: 67 },
  { wk: "W-8",  score: 70 },
  { wk: "W-7",  score: 72 },
  { wk: "W-6",  score: 74 },
  { wk: "W-5",  score: 76 },
  { wk: "W-4",  score: 78 },
  { wk: "W-3",  score: 81 },
  { wk: "W-2",  score: 83 },
  { wk: "W-1",  score: 87 },
];

// P1–P10 phase quality progression — last 4 sessions, golf
export const MY_PHASE_PROGRESS = [
  { phase: "P1",  prev: 88, now: 91 },
  { phase: "P2",  prev: 84, now: 86 },
  { phase: "P3",  prev: 79, now: 88 },
  { phase: "P4",  prev: 76, now: 82 },
  { phase: "P5",  prev: 74, now: 79 },
  { phase: "P6",  prev: 70, now: 72 },
  { phase: "P7",  prev: 81, now: 85 },
  { phase: "P8",  prev: 78, now: 81 },
  { phase: "P9",  prev: 80, now: 83 },
  { phase: "P10", prev: 86, now: 90 },
];

export type PR = {
  id: string;
  metric: string;
  value: string;
  context: string;
  achievedAt: string;
};

export const MY_PRS: PR[] = [
  { id: "pr_1", metric: "Movement quality",     value: "87",   context: "Golf · Face-on swing",     achievedAt: "2026-05-03" },
  { id: "pr_2", metric: "Lead-arm hold @ P3",   value: "172°", context: "Best lead-arm straightness", achievedAt: "2026-05-03" },
  { id: "pr_3", metric: "Hip rotation @ P4",    value: "51.2°",context: "+3.1° vs prior best",      achievedAt: "2026-05-02" },
  { id: "pr_4", metric: "P1–P10 detection",     value: "10/10",context: "All phases auto-detected", achievedAt: "2026-05-02" },
];

export type Goal = {
  id: string;
  label: string;
  target: string;
  progress: number; // 0..100
  due: string;
};

export const MY_GOALS: Goal[] = [
  { id: "g_1", label: "Reduce early extension at P6",         target: "<2° spine drop", progress: 70, due: "2026-06-15" },
  { id: "g_2", label: "Hold lead arm to P3.5",                target: ">175° straight",  progress: 55, due: "2026-06-30" },
  { id: "g_3", label: "Capture 4 swings per week",            target: "16 / month",      progress: 88, due: "2026-05-31" },
];

export const MY_STREAK = {
  weeks: 6,
  days:  4,
  thisWeek: [true, true, false, true, true, false, false], // mon..sun
};

export const MY_KPIS = {
  sessionsThisWeek: 4,
  sessionsTrend7d:  [0, 1, 1, 0, 1, 1, 0],
  avgQuality:       82,
  qualityTrend:     [68, 71, 74, 76, 78, 81, 82],
  bestScore:        87,
  bestTrend:        [78, 80, 82, 83, 85, 86, 87],
  streakWeeks:      MY_STREAK.weeks,
  streakTrend:      [1, 2, 3, 3, 4, 5, 6],
};

export const ACTIVITY_MIX = [
  { activity: "Golf",     count: 38 },
  { activity: "Tennis",   count: 22 },
  { activity: "Cricket",  count: 18 },
  { activity: "Movement", count: 14 },
  { activity: "Bowling",  count: 6  },
];

export function statusColor(s: SessionStatus) {
  switch (s) {
    case "processed":  return "bg-el-50 text-el-700 ring-1 ring-el-200";
    case "processing": return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
    case "queued":     return "bg-slate-100 text-slate-600 ring-1 ring-slate-200";
    case "failed":     return "bg-rose-50 text-rose-700 ring-1 ring-rose-200";
  }
}

export function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function relTime(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}
